import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import debounce from 'lodash.debounce';
import { clone } from 'ramda';
import { css, StyleSheet } from 'aphrodite/no-important';
import {
  Drawer,
  DatePicker,
  PopupMenu,
  TimePicker,
  PressedButton,
  FileUploader,
  Loader,
  Button,
  TextInput,
  TabsContainer,
  TabItem,
  Checkbox,
  ThemeContext
} from 'quantux-ui';
import FileCard from '../file-card';
import CameraCard from '../revenue-cycle-management/camera-card';
import CameraContainer from '../revenue-cycle-management/camera-container';
import CropperContainer from '../revenue-cycle-management/cropper-container';
import CaseField from '../revenue-cycle-management/case-field';
import CaseUserCard from '../revenue-cycle-management/case-user-card';
// Local imports
import { getHighlightedText, BASE_URL, dataURItoBlob, padZero, fileToBlob } from '../../utils';
import { gray, salmon, blue, yellow } from '../../theme';

const bellIcon = (
  <svg
    width="20"
    height="24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M16.8 10.461v6.154l2.4 2.462v1.23H0v-1.23l2.4-2.462v-6.153c0-3.791 1.956-6.942 5.4-7.779v-.837C7.8.825 8.604 0 9.6 0s1.8.825 1.8 1.846v.837c3.432.837 5.4 4 5.4 7.778Zm-8.402 6.154h2.4v-2.461h-2.4v2.461Zm2.4-4.923h-2.4V6.77h2.4v4.923Zm1.205 9.847c0 1.353-1.08 2.461-2.4 2.461-1.332 0-2.4-1.108-2.4-2.461h4.8Z"
      fill="#FF6060"
    />
  </svg>
);

const flagIcon = (
  <svg
    width="16"
    height="16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path fillRule="evenodd" clipRule="evenodd" d="M15.997 0H0v16h16V0h-.003Zm0 0v16l-5.622-8 5.622-8Z" fill="#FF6060" />
  </svg>
);

const tickIcon = (
  <svg
    width='16'
    height='12'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M5.085 9.117 1.292 5.524 0 6.739l5.085 4.817L16 1.215 14.717 0 5.085 9.117Z' fill='#96C8FF' />
  </svg>
);

const loaderStyles = {
  display: 'flex',
  flexFlow: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center'
};

const fileUploaderStyles = {
  borderRadius: '4px',
  flexFlow: 'row',
  justifyContent: 'flex-start',
  padding: '0 8px'
};

const tabItemTextStyles = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const today = new Date();
// const isTouchDevice = true;

const _getLastName = (fullName) => {
  const tmpArr = fullName.split(', ');
  return tmpArr[0] || '';
};

const NewCaseDrawer = (props) => {
  const context = useContext(ThemeContext);
  const { isTouchDevice } = context.theme;

  // #region State Variables
  const [faceSheetKey, setFaceSheetKey] = useState('');
  const [isLoadingFaceSheetData, setIsLoadingFaceSheetData] = useState(false);
  const [isFaceSheetUploading, setFaceSheetUploading] = useState(false);
  const [showFaceSheetCamera, setShowFaceSheetCamera] = useState(false);
  const [faceSheetData, setFaceSheetData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [lastFaceSheetData, setLastFaceSheetData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [showFaceSheetCropper, setShowFaceSheetCropper] = useState(false);
  const [faceSheetCropperEditMode, setFaceSheetCropperEditMode] = useState(true);

  const [consentFormKey, setConsentFormKey] = useState('');
  const [isLoadingConsentFormData, setIsLoadingConsentFormData] = useState(false);
  const [isConsentFormUploading, setConsentFormUploading] = useState(false);
  const [showConsentFormCamera, setShowConsentFormCamera] = useState(false);
  const [consentFormData, setConsentFormData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [lastConsentFormData, setLastConsentFormData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [showConsentFormCropper, setShowConsentFormCropper] = useState(false);
  const [consentFormCropperEditMode, setConsentFormCropperEditMode] = useState(true);

  const [prePostOpFormKey, setPrePostOpFormKey] = useState('');
  const [isLoadingPrePostOpFormData, setIsLoadingPrePostOpFormData] = useState(false);
  const [isPrePostOpFormUploading, setPrePostOpFormUploading] = useState(false);
  const [showPrePostOpFormCamera, setShowPrePostOpFormCamera] = useState(false);
  const [prePostOpFormData, setPrePostOpFormData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [lastPrePostOpFormDataData, setLastPrePostOpFormDataData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [showPrePostOpFormCropper, setShowPrePostOpFormCropper] = useState(false);
  const [prePostOpFormCropperEditMode, setPrePostOpFormCropperEditMode] = useState(true);

  const [intraOpFormKey, setIntraOpFormKey] = useState('');
  const [isLoadingIntraOpFormData, setIsLoadingIntraOpFormData] = useState(false);
  const [isIntraOpFormUploading, setIntraOpFormUploading] = useState(false);
  const [showIntraOpFormCamera, setShowIntraOpFormCamera] = useState(false);
  const [intraOpFormData, setIntraOpFormData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [lastIntraOpFormData, setLastIntraOpFormData] = useState({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
  const [showIntraOpFormCropper, setShowIntraOpFormCropper] = useState(false);
  const [intraOpFormCropperEditMode, setIntraOpFormCropperEditMode] = useState(true);
  // Surgery Date
  const [showSurgeryDateAlert, setShowSurgeryDateAlert] = useState(true);
  const [isSurgeryDatePristine, setIsSurgeryDatePristine] = useState(true);
  const [surgeryDate, setSurgeryDate] = useState(today.getDate());
  const [surgeryMonth, setSurgeryMonth] = useState(today.getMonth());
  const [surgeryYear, setSurgeryYear] = useState(today.getFullYear());

  const [patientType, setPatientType] = useState('');
  const [showPatientTypeMenu, setShowPatientTypeMenu] = useState(false);
  // Facility List
  const [facilityValue, setFacilityValue] = useState('');
  const [showFacilitySearchMenu, setShowFacilitySearchMenu] = useState(false);
  const [facilitySearchResults, setFacilitySearchResults] = useState([]);
  const [facilityList, setFacilityList] = useState([]);
  // Surgeons List
  const [surgeonValue, setSurgeonValue] = useState('');
  const [showSurgeonsSearchMenu, setShowSurgeonsSearchMenu] = useState(false);
  const [surgeonsSearchResults, setSurgeonsSearchResults] = useState([]);
  const [surgeonsList, setSurgeonsList] = useState([]);
  const [showSurgeonsDataInput, setShowSurgeonsDataInput] = useState(false);
  const [isNoneSurgeon, setIsNoneSurgeon] = useState(false);
  // Anesthesiologists  List
  const [anesthesiologistValue, setAnesthesiologistValue] = useState('');
  const [showAnesthesiologistsSearchMenu, setShowAnesthesiologistsSearchMenu] = useState(false);
  const [anesthesiologistsSearchResults, setAnesthesiologistsSearchResults] = useState([]);
  const [anesthesiologistsList, setAnesthesiologistsList] = useState([]);
  const [isNoneAnesthesiologist, setIsNoneAnesthesiologist] = useState(false);
  // CRNAs List
  const [crnaValue, setCrnaValue] = useState('');
  const [showCrnasSearchMenu, setShowCrnasSearchMenu] = useState(false);
  const [crnasSearchResults, setCrnasSearchResults] = useState([]);
  const [crnasList, setCrnasList] = useState([]);
  const [isNoneCrna, setIsNoneCrna] = useState(false);
  // Procedures List
  const [procedureValue, setProcedureValue] = useState('');
  const [showSurgicalProceduresSearchMenu, setShowSurgicalProceduresSearchMenu] = useState(false);
  const [surgicalProceduresSearchResults, setSurgicalProceduresSearchResults] = useState([]);
  const [proceduresList, setProceduresList] = useState([]);
  const [surgicalProceduresForSurgeon, setSurgicalProceduresForSurgeon] = useState([]);
  // PreOp Dx
  const [preOpDxValue, setPreOpDxValue] = useState('');
  const [showPreOpDxSearchMenu, setShowPreOpDxSearchMenu] = useState(false);
  const [preOpDxSearchResults, setPreOpDxSearchResults] = useState([]);
  const [preOpDxList, setPreOpDxList] = useState([]);
  // PostOp Dx
  const [postOpDxList, setPostOpDxList] = useState([]);
  const [showPostOpDxSearchMenu, setShowPostOpDxSearchMenu] = useState(false);
  const [postOpDxSearchResults, setPostOpDxSearchResults] = useState([]);
  const [postOpDxValue, setPostOpDxValue] = useState('');
  const [isSameAsPreOpDx, setIsSameAsPreOpDx] = useState(false);
  // Anesthesia Procedures
  const [anesthesiaProcedureValue, setAnesthesiaProcedureValue] = useState('');
  const [showAnesthesiaProceduresSearchMenu, setShowAnesthesiaProceduresSearchMenu] = useState(false);
  const [anesthesiaProceduresSearchResults, setAnesthesiaProceduresSearchResults] = useState([]);
  const [anesthesiaProceduresList, setAnesthesiaProceduresList] = useState([]);
  const [isNoneAnesthesiaProcedure, setIsNoneAnesthesiaProcedure] = useState(false);
  const [surgicalProceduresActiveTab, setSurgicalProceduresActiveTab] = useState(0);
  const [lastSurg, setLastSurg] = useState(0);
  const [anesthesiaProceduresActiveTab, setAnesthesiaProceduresActiveTab] = useState(0);
  const [favAnesthesiaProcedures, setFavAnesthesiaProcedures] = useState([]);
  // Anesthesia Times (Start)
  const [anesthesiaStartDate, setAnesthesiaStartDate] = useState(today.getDate());
  const [anesthesiaStartMonth, setAnesthesiaStartMonth] = useState(today.getMonth());
  const [anesthesiaStartYear, setAnesthesiaStartYear] = useState(today.getFullYear());
  const [isAnesStartDatePristine, setIsAnesStartDatePristine] = useState(true);
  const [anesthesiaStartHour, setAnesthesiaStartHour] = useState(today.getHours());
  const [anesthesiaStartMinute, setAnesthesiaStartMinute] = useState(today.getMinutes());
  const [isAnesthesiaStartTimePristine, setIsAnesthesiaStartTimePristine] = useState(true);
  // Anesthesia Times (End)
  const [anesthesiaEndDate, setAnesthesiaEndDate] = useState(today.getDate());
  const [anesthesiaEndMonth, setAnesthesiaEndMonth] = useState(today.getMonth());
  const [anesthesiaEndYear, setAnesthesiaEndYear] = useState(today.getFullYear());
  const [isAnesEndDatePristine, setIsAnesEndDatePristine] = useState(true);
  const [anesthesiaEndHour, setAnesthesiaEndHour] = useState(today.getHours());
  const [anesthesiaEndMinute, setAnesthesiaEndMinute] = useState(today.getMinutes());
  const [isAnesthesiaEndTimePristine, setIsAnesthesiaEndTimePristine] = useState(true);
  // Calculated Anesthesia Time
  const [anesthesiaTime, setAnesthesiaTime] = useState(0);
  // ASA Score
  const [asaScore, setAsaScore] = useState('');
  const [isEmergencyAsaScore, setIsEmergencyAsaScore] = useState(false);
  // Anesthesia Type
  const [isGaEtt, setIsGaEtt] = useState(false);
  const [isGaLma, setIsGaLma] = useState(false);
  const [isMac, setIsMac] = useState(false);
  const [isEpidural, setIsEpidural] = useState(false);
  const [isSab, setIsSab] = useState(false);
  const [isRegional, setIsRegional] = useState(false);
  const [isLocal, setIsLocal] = useState(false);
  const [isOther, setIsOther] = useState(false);
  // Anesthesia Position
  const [isSupine, setIsSupine] = useState(false);
  const [isProne, setIsProne] = useState(false);
  const [isLithotomy, setIsLithotomy] = useState(false);
  const [isLateralLeft, setIsLateralLeft] = useState(false);
  const [isLateralRight, setIsLateralRight] = useState(false);
  const [isSitting, setIsSitting] = useState(false);
  // Anesthsia Modifiers
  const [isFieldAvoidance, setIsFieldAvoidance] = useState(false);
  const [isExtremeAge, setIsExtremeAge] = useState(false);
  const [isControlledHypotension, setIsControlledHypotension] = useState(false);
  const [isControlledHypothermia, setIsControlledHypothermia] = useState(false);
  const [isPronePosition, setIsPronePosition] = useState(false);
  // Can create case
  const [canCreateCase, setCanCreateCase] = useState(false);
  // Flags
  const [showFaceSheetAlert, setShowFaceSheetAlert] = useState(true);
  const [showConsentFormAlert, setShowConsentFormAlert] = useState(true);
  const [showPrePostOpFormAlert, setShowPrePostOpFormAlert] = useState(true);
  const [showIntraOpFormAlert, setShowIntraOpFormAlert] = useState(true);
  const [showPatientTypeAlert, setShowPatientTypeAlert] = useState(true);
  //
  const [showFacilityAlert, setShowFacilityAlert] = useState(true);
  const [showSurgeonsAlert, setShowSurgeonsAlert] = useState(true);
  const [showAnesthesiologistsAlert, setShowAnesthesiologistsAlert] = useState(true);
  const [showCrnasAlert, setShowCrnasAlert] = useState(true);
  const [showSurgicalProceduresAlert, setShowSurgicalProceduresAlert] = useState(true);
  const [showPreOpDxAlert, setShowPreOpDxAlert] = useState(true);
  const [showPostOpDxAlert, setShowPostOpDxAlert] = useState(true);
  const [showAnesthesiaProceduresAlert, setShowAnesthesiaProceduresAlert] = useState(true);
  // Other Flags (bell icon)
  const [isOtherFacility, setIsOtherFacility] = useState(false);
  const [isOtherSurgeons, setIsOtherSurgeons] = useState(false);
  const [isOtherAnesthesiologists, setIsOtherAnesthesiologists] = useState(false);
  const [isOtherCrans, setIsOtherCrans] = useState(false);
  const [isOtherSurgicalProcedures, setIsOtherSurgicalProcedures] = useState(false);
  const [isOtherPreOpDx, setIsOtherPreOpDx] = useState(false);
  const [isOtherPostOpDx, setIsOtherPostOpDx] = useState(false);
  const [isOtherAnesthesiaProcedures, setIsOtherAnesthesiaProcedures] = useState(false);
  //
  const [showAsaAlert, setShowAsaAlert] = useState(true);
  const [showPositionAlert, setShowPositionAlert] = useState(true);
  const [showAnesthesiaTypeAlert, setShowAnesthesiaTypeAlert] = useState(true);
  //
  const [showAnesthesiaTimeAlert, setShowAnesthesiaTimeAlert] = useState(false);
  const [showAnesStartDateAlert, setShowAnesStartDateAlert] = useState(true);
  const [showAnesStartTimeAlert, setShowAnesStartTimeAlert] = useState(true);
  const [showAnesEndDateAlert, setShowAnesEndDateAlert] = useState(true);
  const [showAnesEndTimeAlert, setShowAnesEndTimeAlert] = useState(true);
  // Loader
  const [showSaveCaseLoader, setShowSaveCaseLoader] = useState(false);
  // Drawer width
  const [drawerWidth, setDrawerWidth] = useState(window.innerWidth <= 700 ? window.innerWidth : 375);
  // PDF documents flags
  const [isFaceSheetPDF, setIsFaceSheetPDF] = useState(false);
  const [isConsentFormPDF, setIsConsentFormPDF] = useState(false);
  const [isPrePostOpFormPDF, setIsPrePostOpFormPDF] = useState(false);
  const [isIntraOpFormPDF, setIsIntraOpFormPDF] = useState(false);
  // #endregion

  // #region Refs
  const patientTypeMenuRef = useRef();
  // File Input Refs
  const faceSheetFileInputRef = useRef();
  const consentFormFileInputRef = useRef();
  const prePostOpFormFileInputRef = useRef();
  const intraOpFormFileInputRef = useRef();
  // Cropper refs
  const faceSheetCropperRef = useRef();
  const consentFormCropperRef = useRef();
  const prePostOpFormCropperRef = useRef();
  const intraOpFormCropperRef = useRef();
  // Form Sections Refs
  const drawerRef = useRef();
  window.drawerRef = drawerRef;
  const spacerRef = useRef();
  window.spacerRef = spacerRef;
  const facilitySectionRef = useRef();
  window.facRef = facilitySectionRef;
  const surgeonsSectionRef = useRef();
  const anesthesiologistsSectionRef = useRef();
  const crnasSectionRef = useRef();
  const surgicalProceduresSectionRef = useRef();
  const preOpDxSectionRef = useRef();
  const postOpDxSectionRef = useRef();
  const anesthesiaProceduresSectionRef = useRef();
  // Search Menus
  const facilitySearchMenuRef = useRef();
  const surgeonsSearchMenuRef = useRef();
  const anesthesiologistsSearchMenuRef = useRef();
  const crnasSearchMenuRef = useRef();
  const surgicalProceduresSearchMenuRef = useRef();
  const preOpDxSearchMenuRef = useRef();
  const postOpDxSearchMenuRef = useRef();
  const anesthesiaProceduresSearchMenuRef = useRef();
  // #endregion

  // let drawerWidth = window.innerWidth <= 700 ? window.innerWidth : 375;
  // if (showFaceSheetCamera || showFaceSheetCropper ||
  //   showConsentFormCamera || showConsentFormCropper ||
  //   showPrePostOpFormCamera || showPrePostOpFormCropper ||
  //   showIntraOpFormCamera || showIntraOpFormCropper
  // ) {
  //   drawerWidth = window.innerWidth;
  // }

  useEffect(() => {
    if (showFaceSheetCamera || showFaceSheetCropper ||
      showConsentFormCamera || showConsentFormCropper ||
      showPrePostOpFormCamera || showPrePostOpFormCropper ||
      showIntraOpFormCamera || showIntraOpFormCropper
    ) {
      setDrawerWidth(window.innerWidth);
    } else {
      setDrawerWidth(window.innerWidth <= 700 ? window.innerWidth : 375);
    }
  }, [
    showFaceSheetCamera,
    showFaceSheetCropper,
    showConsentFormCamera,
    showConsentFormCropper,
    showPrePostOpFormCamera,
    showPrePostOpFormCropper,
    showIntraOpFormCamera,
    showIntraOpFormCropper
  ]);

  // #region Network Requests
  const getSurgicalProceduresForSurgeon = (surgeonId) => {
    const url = new URL(`${BASE_URL}/users/getSurgicalProceduresForSurgeon`);
    const params = { surgeonId };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        console.log(data.procedures);
        setSurgicalProceduresForSurgeon(data.procedures);
        // setSurgicalProceduresForSurgeon(data.procedures
        //   .filter(el => (el.shortDescription.toLowerCase().indexOf(procedureValue.toLowerCase()) > -1)
        // ));
      })
      .catch(error => console.log('error', error));
  };

  const searchFacilities = (value) => {
    const url = new URL(`${BASE_URL}/users/searchFacilities`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        console.log(data);
        // setShowFacilitySearchMenu(true);
        setFacilitySearchResults(data.facilities);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedFacilitySearch = useCallback(debounce(value => searchFacilities(value), 800), []);

  const searchSurgeons = (value) => {
    const url = new URL(`${BASE_URL}/users/searchMedProsSurgeons`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        // setShowSurgeonsSearchMenu(true);
        setSurgeonsSearchResults(data.users);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedSurgeonsSearch = useCallback(debounce(value => searchSurgeons(value), 800), []);

  const searchAnesthesiologists = (value) => {
    const url = new URL(`${BASE_URL}/users/searchMedProsSurgeons`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowAnesthesiologistsSearchMenu(true);
        setAnesthesiologistsSearchResults(data.users);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedAnesthesiologistsSearch = useCallback(debounce(value => searchAnesthesiologists(value), 800), []);

  const searchCrnas = (value) => {
    const url = new URL(`${BASE_URL}/users/searchMedProsSurgeons`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        // setShowCrnasSearchMenu(true);
        setCrnasSearchResults(data.users);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedCrnasSearch = useCallback(debounce(value => searchCrnas(value), 800), []);

  const searchSurgicalProcedures = (value) => {
    const url = new URL(`${BASE_URL}/cpt/searchCpt2`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        // setShowSurgicalProceduresSearchMenu(true);
        setSurgicalProceduresSearchResults(data.cptCodes);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedSurgicalProceduresSearch = useCallback(debounce(value => searchSurgicalProcedures(value), 800), []);

  const searchPreOpDx = (value) => {
    const url = new URL(`${BASE_URL}/icd10/searchIcd10`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowPreOpDxSearchMenu(true);
        setPreOpDxSearchResults(data.icd10Codes);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedPreOpDxSearch = useCallback(debounce(value => searchPreOpDx(value), 800), []);

  const searchPostOpDx = (value) => {
    const url = new URL(`${BASE_URL}/icd10/searchIcd10`);
    const params = { searchTerm: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowPostOpDxSearchMenu(true);
        setPostOpDxSearchResults(data.icd10Codes);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedPostOpDxSearch = useCallback(debounce(value => searchPostOpDx(value), 800), []);

  const searchAnesthesiaProcedures = (value) => {
    const url = new URL(`${BASE_URL}/anesProc/all`);
    const params = { search: value.trim() };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        setShowAnesthesiaProceduresSearchMenu(true);
        setAnesthesiaProceduresSearchResults(data.anesthesiaProcedures);
      })
      .catch(error => console.log('error', error));
  };
  const debouncedAnesthesiaProceduresSearch = useCallback(debounce(value => searchAnesthesiaProcedures(value), 800), []);

  // #endregion

  // #region Effects

  // Face Sheet, Consent Form, Pre/Post Op Form and Intra Op keys
  useEffect(() => { setShowFaceSheetAlert(faceSheetKey.length === 0); }, [faceSheetKey]);
  useEffect(() => { setShowConsentFormAlert(consentFormKey.length === 0); }, [consentFormKey]);
  useEffect(() => { setShowPrePostOpFormAlert(prePostOpFormKey.length === 0); }, [prePostOpFormKey]);
  useEffect(() => { setShowIntraOpFormAlert(intraOpFormKey.length === 0); }, [intraOpFormKey]);
  // Date of Surgery and Patient Type
  // useEffect(() => { setShowSurgeryDateAlert(!showSurgeryDateAlert); }, [surgeryDate, surgeryMonth, surgeryYear]);
  useEffect(() => { setShowPatientTypeAlert(patientType === ''); }, [patientType]);
  useEffect(() => { setShowSurgeryDateAlert(isSurgeryDatePristine); }, [isSurgeryDatePristine]);
  // Facility, Surgeons, Anesthesiologists and CRNAs
  useEffect(() => {
    setShowFacilityAlert(facilityList.length === 0);
    const tmpIndex = facilityList.findIndex(el => el.npi === 0);
    setIsOtherFacility(tmpIndex >= 0);
  }, [facilityList]);

  useEffect(() => {
    setShowSurgeonsAlert(surgeonsList.length === 0);
    const tmpIndex = surgeonsList.findIndex(el => el.npi === 0);
    setIsOtherSurgeons(tmpIndex >= 0);
  }, [surgeonsList]);

  useEffect(() => {
    setShowAnesthesiologistsAlert(anesthesiologistsList.length === 0 && !isNoneAnesthesiologist);
    if (!isNoneAnesthesiologist) {
      const tmpIndex = anesthesiologistsList.findIndex(el => el.npi === 0);
      setIsOtherAnesthesiologists(tmpIndex >= 0);
    }
  }, [anesthesiologistsList, isNoneAnesthesiologist]);

  useEffect(() => {
    setShowSurgeonsAlert(surgeonsList.length === 0 && !isNoneSurgeon);
    if (!isNoneSurgeon) {
      const tmpIndex = surgeonsList.findIndex(el => el.npi === 0);
      setIsOtherSurgeons(tmpIndex >= 0);
    }
  }, [surgeonsList, isNoneSurgeon]);

  useEffect(() => {
    setShowCrnasAlert(crnasList.length === 0 && !isNoneCrna);
    if (!isNoneCrna) {
      const tmpIndex = crnasList.findIndex(el => el.npi === 0);
      setIsOtherCrans(tmpIndex >= 0);
    }
  }, [crnasList, isNoneCrna]);

  // Surgical Procedures, PreOpDx, postOpDx and Anesthesia Procedures
  useEffect(() => {
    setShowSurgicalProceduresAlert(proceduresList.length === 0);
    const tmpIndex = proceduresList.findIndex(el => el.npi === 0);
    setIsOtherSurgicalProcedures(tmpIndex >= 0);
  }, [proceduresList]);

  useEffect(() => {
    setShowPreOpDxAlert(preOpDxList.length === 0);
    const tmpIndex = preOpDxList.findIndex(el => el.npi === 0);
    setIsOtherPreOpDx(tmpIndex >= 0);
  }, [preOpDxList]);

  useEffect(() => {
    setShowPostOpDxAlert(postOpDxList.length === 0 && !isSameAsPreOpDx);
    if (!isSameAsPreOpDx) {
      const tmpIndex = postOpDxList.findIndex(el => el.npi === 0);
      setIsOtherPostOpDx(tmpIndex >= 0);
    }
  }, [[postOpDxList, isSameAsPreOpDx]]);

  useEffect(() => {
    setShowAnesthesiaProceduresAlert(anesthesiaProceduresList.length === 0 && !isNoneAnesthesiaProcedure);
    if (!isNoneAnesthesiaProcedure) {
      const tmpIndex = anesthesiaProceduresList.findIndex(el => el.npi === 0);
      setIsOtherAnesthesiaProcedures(tmpIndex >= 0);
    }
  }, [anesthesiaProceduresList, isNoneAnesthesiaProcedure]);

  useEffect(() => {
    setShowAnesthesiaTypeAlert(!isGaEtt && !isGaLma && !isMac && !isEpidural && !isSab && !isRegional && !isLocal && !isOther);
  }, [
    isGaEtt,
    isGaLma,
    isMac,
    isEpidural,
    isSab,
    isRegional,
    isLocal,
    isOther
  ]);

  // Anesthesia Start Date and Time
  useEffect(() => { setShowAnesStartDateAlert(isAnesStartDatePristine); }, [isAnesStartDatePristine]);
  useEffect(() => { setShowAnesStartTimeAlert(isAnesthesiaStartTimePristine); }, [isAnesthesiaStartTimePristine]);
  // Anesthesia End Date and Time
  useEffect(() => { setShowAnesEndDateAlert(isAnesEndDatePristine); }, [isAnesEndDatePristine]);
  useEffect(() => { setShowAnesEndTimeAlert(isAnesthesiaEndTimePristine); }, [isAnesthesiaEndTimePristine]);
  // ASA Score
  useEffect(() => { setShowAsaAlert(asaScore.length === 0); }, [asaScore]);

  // Position
  useEffect(() => {
    setShowPositionAlert(!isSupine && !isProne && !isLithotomy && !isLateralLeft && !isLateralRight && !isSitting);
  }, [
    isSupine,
    isProne,
    isLithotomy,
    isLateralLeft,
    isLateralRight,
    isSitting
  ]);

  // Calculating Total Anesthesia Time
  useEffect(() => {
    const anesthesiaStartDateTime = new Date(
      anesthesiaStartYear,
      anesthesiaStartMonth,
      anesthesiaStartDate,
      anesthesiaStartHour,
      anesthesiaStartMinute
    );

    const anesthesiaEndDateTime = new Date(
      anesthesiaEndYear,
      anesthesiaEndMonth,
      anesthesiaEndDate,
      anesthesiaEndHour,
      anesthesiaEndMinute
    );

    const timeDiff = (anesthesiaEndDateTime.getTime() - anesthesiaStartDateTime.getTime()) / (1000 * 60);
    if (timeDiff <= 0 &&
      !isAnesStartDatePristine &&
      !isAnesEndDatePristine &&
      !isAnesthesiaStartTimePristine &&
      !isAnesthesiaEndTimePristine) {
      setShowAnesthesiaTimeAlert(true);
      setAnesthesiaTime(0);
    } else {
      console.log(`Anesthesia Time = ${timeDiff.toFixed(0)}`);
      setShowAnesthesiaTimeAlert(false);
      setAnesthesiaTime(timeDiff.toFixed(0));
    }
  }, [
    anesthesiaStartDate,
    anesthesiaStartMonth,
    anesthesiaStartYear,
    anesthesiaStartHour,
    anesthesiaStartMinute,
    anesthesiaEndDate,
    anesthesiaEndMonth,
    anesthesiaEndYear,
    anesthesiaEndHour,
    anesthesiaEndMinute
  ]);

  // Setting create case flag
  useEffect(() => {
    // console.log(`showFaceSheetAlert = ${showFaceSheetAlert}`);
    // console.log(`showConsentFormAlert = ${showConsentFormAlert}`);
    // console.log(`showPrePostOpFormAlert = ${showPrePostOpFormAlert}`);
    // console.log(`showIntraOpFormAlert = ${showIntraOpFormAlert}`);
    // console.log(`showSurgeryDateAlert = ${showSurgeryDateAlert}`);
    // console.log(`showPatientTypeAlert = ${showPatientTypeAlert}`);
    // console.log(`showFacilityAlert = ${showFacilityAlert}`);
    // console.log(`showSurgeonsAlert = ${showSurgeonsAlert}`);
    // console.log(`showAnesthesiologistsAlert = ${showAnesthesiologistsAlert}`);
    // console.log(`showCrnasAlert = ${showCrnasAlert}`);
    // console.log(`showSurgicalProceduresAlert = ${showSurgicalProceduresAlert}`);
    // console.log(`showPreOpDxAlert = ${showPreOpDxAlert}`);
    // console.log(`showPostOpDxAlert = ${showPostOpDxAlert}`);
    // console.log(`showAnesthesiaProceduresAlert = ${showAnesthesiaProceduresAlert}`);
    // console.log(`showAsaAlert = ${showAsaAlert}`);
    // console.log(`showPositionAlert = ${showPositionAlert}`);
    // console.log(`showAnesStartDateAlert = ${showAnesStartDateAlert}`);
    // console.log(`showAnesStartTimeAlert = ${showAnesStartTimeAlert}`);
    // console.log(`showAnesEndDateAlert = ${showAnesEndDateAlert}`);
    // console.log(`showAnesEndTimeAlert = ${showAnesEndTimeAlert}`);

    setCanCreateCase(
      !showFaceSheetAlert /* &<& !showConsentFormAlert */ && !showPrePostOpFormAlert && !showIntraOpFormAlert &&
      !showSurgeryDateAlert && !showPatientTypeAlert &&
      !showFacilityAlert && !showSurgeonsAlert && !showAnesthesiologistsAlert && !showCrnasAlert &&
      !showSurgicalProceduresAlert && !showPreOpDxAlert && !showPostOpDxAlert && !showAnesthesiaProceduresAlert &&
      !showAsaAlert && !showPositionAlert &&
      !showAnesStartDateAlert && !showAnesStartTimeAlert && !showAnesEndDateAlert && !showAnesEndTimeAlert
    );
  }, [
    showFaceSheetAlert,
    showConsentFormAlert,
    showPrePostOpFormAlert,
    showIntraOpFormAlert,
    showSurgeryDateAlert,
    showPatientTypeAlert,
    showFacilityAlert,
    showSurgeonsAlert,
    showAnesthesiologistsAlert,
    showCrnasAlert,
    showSurgicalProceduresAlert,
    showPreOpDxAlert,
    showPostOpDxAlert,
    showAnesthesiaProceduresAlert,
    showAsaAlert,
    showPositionAlert,
    showAnesStartDateAlert,
    showAnesStartTimeAlert,
    showAnesEndDateAlert,
    showAnesEndTimeAlert
  ]);
  // #endregion

  useEffect(() => {
    if (facilityValue.length === 0) {
      setFacilitySearchResults([]);
    }
    if (facilityValue.trim().length > 0) {
      debouncedFacilitySearch(facilityValue);
    }
  }, [facilityValue]);

  useEffect(() => {
    if (surgeonValue.length === 0) {
      setSurgeonsSearchResults([]);
    }
    if (surgeonValue.trim().length > 0) {
      debouncedSurgeonsSearch(surgeonValue);
    }
  }, [surgeonValue]);

  useEffect(() => {
    if (anesthesiologistValue.length === 0) {
      setAnesthesiologistsSearchResults([]);
    }
    if (anesthesiologistValue.trim().length > 0) {
      debouncedAnesthesiologistsSearch(anesthesiologistValue);
    }
  }, [anesthesiologistValue]);

  useEffect(() => {
    if (crnaValue.length === 0) {
      setCrnasSearchResults([]);
    }
    if (crnaValue.trim().length > 0) {
      debouncedCrnasSearch(crnaValue);
    }
  }, [crnaValue]);

  useEffect(() => {
    if (procedureValue.length === 0) {
      setSurgicalProceduresSearchResults([]);
    }
    if (procedureValue.trim().length > 0) {
      debouncedSurgicalProceduresSearch(procedureValue);
    }
  }, [procedureValue]);

  useEffect(() => {
    if (preOpDxValue.trim().length > 0) {
      debouncedPreOpDxSearch(preOpDxValue);
    }
  }, [preOpDxValue]);

  useEffect(() => {
    if (postOpDxValue.trim().length > 0) {
      debouncedPostOpDxSearch(postOpDxValue);
    }
  }, [postOpDxValue]);

  useEffect(() => {
    if (anesthesiaProcedureValue.length === 0) {
      setAnesthesiaProceduresSearchResults([]);
    }
    if (anesthesiaProcedureValue.trim().length > 0) {
      debouncedAnesthesiaProceduresSearch(anesthesiaProcedureValue);
    }
  }, [anesthesiaProcedureValue]);

  useEffect(() => {
    if (faceSheetData.images.length === 0) {
      setFaceSheetKey('');
    }
  }, [faceSheetData]);

  useEffect(() => {
    console.log(consentFormData);
    if (consentFormData.images.length === 0) {
      setConsentFormKey('');
    }
  }, [consentFormData]);

  useEffect(() => {
    if (prePostOpFormData.images.length === 0) {
      setPrePostOpFormKey('');
    }
  }, [prePostOpFormData]);

  useEffect(() => {
    if (intraOpFormData.images.length === 0) {
      setIntraOpFormKey('');
    }
  }, [intraOpFormData]);

  // Styles
  const styles = StyleSheet.create({
    drawerContainer: {
      background: gray[800],
      color: gray[300],
      height: '100%',
      // height: `${window.innerHeight}px`,
      // position: 'relative',
      display: 'flex',
      flexFlow: 'column',
      overflow: 'hidden'
    },
    drawerHeader: {
      height: '40px',
      position: 'relative',
      padding: '8px',
      fontWeight: 'bold',
      borderBottom: `1px solid ${gray[600]}`,
      flexShrink: '0',
      background: gray[600],
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    drawerHeaderCloseIcon: {
      color: gray[300],
      fontSize: '24px',
      ':hover': {
        color: salmon[500]
      }
    },
    drawerContentArea: {
      overflowY: 'auto',
      color: gray[300],
      flexGrow: '1',
      padding: '8px'
    },
    drawerFooter: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      background: gray[700],
      width: '100%',
      borderTop: `1px solid ${gray[500]}`,
      flexShrink: '0',
      textAlign: 'right',
      paddingRight: '8px',
      height: '56px',
      paddingTop: '6px'
    },
    fieldGroup: {
      position: 'relative',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      // justifyContent: 'space-between',
      // background: blue[700],
      borderRadius: '4px',
      color: gray[100],
      width: '100%',
      padding: '4px',
      height: '48px'
    },
    patientTypeMenuHeading: {
      color: showPatientTypeAlert ? salmon[500] : blue[400],
      cursor: 'pointer'
    },
    patientTypeMenuContainer: {
      width: '120px',
      background: blue[600]
    },
    patientTypeMenuItem: {
      height: '40px',
      background: blue[700],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      cursor: 'pointer',
      ':hover': {
        color: blue[800],
        background: blue[300]
      }
    },
    menuItem: {
      cursor: 'pointer',
      // background: blue[900],
      background: '#103860',
      borderRadius: '8px',
      color: gray[300],
      padding: '4px',
      lineHeight: '16px',
      // borderBottom: `1px solid ${gray[600]}`,
      fontSize: '14px',
      marginBottom: '2px',
      // height: '40px',
      ':hover': {
        // background: gray[500],
        // background: gray[700],
        background: '#184878',
        color: 'white'
      },
      ':last-child': {
        borderBottom: '0'
      }
    },
    menuItemAvatarContainer: {
      width: '32px',
      height: '32px'
    },
    menuItemAvatarImg: {
      // width: 'auto',
      width: '32px',
      height: '32px',
      maxHeight: '32px',
      borderRadius: '50%'
    },
    searchMenu: {
      background: blue[900],
      borderRadius: '0 5px',
      paddingBottom: '1px'
    },
    sectionHeading: {
      color: blue[200],
      fontSize: '14px',
      fontWeight: '700'
    },
    sectionSubHeading: {
      color: blue[200]
    },
    requiredHeadingMsg: {
      color: salmon[500]
    },
    labelHeading: {
      fontSize: '12px',
      marginBottom: '4px',
      color: gray[200]
    },
    anesModBtns: {
      fontSize: '12px'
    },
    caseFieldCheckbox: {
      height: '14px',
      width: '14px',
      marginLeft: '0'
    },
    crossedCheckbox: {
      appearance: 'initial',
      height: '14px',
      width: '14px',
      marginLeft: '0',
      position: 'relative'
    },
    loaderContainer: {
      height: '40px',
      background: blue[700],
      padding: '8px 0',
      position: 'relative',
      display: 'flex'
    },
    loaderContainerCloseIcon: {
      fontSize: '24px',
      position: 'relative',
      right: '4px'
    },
    pressedBtn: {
      background: blue[900],
      color: blue[200]
    },
    pressedBtnAlert: {
      background: '#804254',
      border: '1px solid #804254 !important',
      color: salmon[500]
    },
    searchPopupMenuContainer: {
      width: '358px',
      background: blue[900],
      overflowY: 'hidden',
      paddingBottom: '1px',
      borderRadius: '5px'
    },
    searchPopupMenuPlaceholder: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100px'
    },
    tabContainerCloseBtn: {
      background: '#A0C8F0',
      width: '20px',
      height: '20px',
      fontSize: '18px',
      flexGrow: '0',
      flexShrink: '0',
      borderRadius: '4px',
      ':hover': {
        color: salmon[700]
      }
    },
    menuItemStarContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: '0',
      fontSize: '28px',
      width: '40px',
      ':hover': {
        color: yellow[500]
      }
    },
    isFav: {
      color: yellow[500]
    }
  });

  const tabItemStyles = {
    paddingRight: '0',
    background: '#184878'
    // background: blue[900]
  };

  // #region Case Data Handlers
  const handleOnChangeAnesthesiaStartDate = (year, month, date) => {
    setAnesthesiaStartDate(date);
    setAnesthesiaStartMonth(month);
    setAnesthesiaStartYear(year);
    setIsAnesStartDatePristine(false);
  };

  const handleOnChangeAnesthesiaStartTime = (hour, min) => {
    setIsAnesthesiaStartTimePristine(false);
    setAnesthesiaStartHour(hour);
    setAnesthesiaStartMinute(min);
  };

  const handleOnChangeAnesthesiaEndDate = (year, month, date) => {
    setAnesthesiaEndDate(date);
    setAnesthesiaEndMonth(month);
    setAnesthesiaEndYear(year);
    setIsAnesEndDatePristine(false);
  };

  const handleOnChangeAnesthesiaEndTime = (hour, min) => {
    console.log(`Anesthesia End Time => hour = ${hour} & minute = ${min}`);
    setIsAnesthesiaEndTimePristine(false);
    setAnesthesiaEndHour(hour);
    setAnesthesiaEndMinute(min);
  };
  // #endregion

  // #region Common File Ops Handlers
  const uploadPrivateFile = (img, inputRef, formType) => {
    const formData = new FormData();
    if (img) {
      formData.append('file', img);
    } else {
      const img = inputRef.current.files[0];
      formData.append('file', img);
    }
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const options = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/uploadForm`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        // console.log(res);
        if (res.success) {
          switch (formType) {
            case 'faceSheet':
              setFaceSheetUploading(false);
              setFaceSheetKey(res.fileName);
              setIsFaceSheetPDF(img.type === 'application/pdf');
              if (res.fileData) {
                setFaceSheetData(res.fileData);
              }
              break;
            case 'consentForm':
              setConsentFormUploading(false);
              setConsentFormKey(res.fileName);
              setIsConsentFormPDF(img.type === 'application/pdf');
              if (res.fileData) {
                setConsentFormData(res.fileData);
              }
              break;
            case 'prePostOpForm':
              setPrePostOpFormUploading(false);
              setPrePostOpFormKey(res.fileName);
              setIsPrePostOpFormPDF(img.type === 'application/pdf');
              if (res.fileData) {
                setPrePostOpFormData(res.fileData);
              }
              break;
            case 'intraOpForm':
              setIntraOpFormUploading(false);
              setIntraOpFormKey(res.fileName);
              setIsIntraOpFormPDF(img.type === 'application/pdf');
              if (res.fileData) {
                setIntraOpFormData(res.fileData);
              }
              break;
          }
        }
      })
      .catch(error => console.log('error', error));
  };

  const createPdf = (images, formType) => {
    const formData = new FormData();
    images.forEach((el, idx) => {
      formData.append('images', dataURItoBlob(el));
    });

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const options = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/createPdf`, options)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          switch (formType) {
            case 'faceSheet':
              setFaceSheetUploading(false);
              setFaceSheetKey(res.fileName);
              break;
            case 'consentForm':
              setConsentFormUploading(false);
              setConsentFormKey(res.fileName);
              break;
            case 'prePostOpForm':
              setPrePostOpFormUploading(false);
              setPrePostOpFormKey(res.fileName);
              break;
            case 'intraOpForm':
              setIntraOpFormUploading(false);
              setIntraOpFormKey(res.fileName);
              break;
          }
        }
      })
      .catch(error => console.log('error', error));
  };

  // For Print and View (all forms including face sheet)
  const handleOnClickViewFile = (formKey) => {
    const formData = new FormData();
    formData.append('formKey', formKey);

    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };

    fetch(`${BASE_URL}/cases/downloadForm`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        if (res.success) {
          window.open(res.url, '_blank');
        }
      })
      .catch(err => {
        console.log(err);
      });
  };
  // #endregion

  // #region FaceSheet File Upload Handlers
  const handleOnFaceSheetDrop = (img) => {
    setFaceSheetUploading(true);
    // Creating FaceSheet data object from dopped image file
    const reader = new FileReader();
    reader.onloadend = () => {
      setFaceSheetData((prvState) => ({
        activeIndex: 0,
        images: prvState.images.concat(reader.result),
        croppedImages: prvState.croppedImages.concat(reader.result)
      }));
    };
    reader.readAsDataURL(img);
    uploadPrivateFile(img, null, 'faceSheet');
  };

  const handleOnFaceSheetSelected = () => {
    if (faceSheetFileInputRef.current.files.length > 0) {
      setFaceSheetUploading(true);
      // Creating FaceSheet data object from selected image file
      for (let i = 0; i < faceSheetFileInputRef.current.files.length; i++) {
        const img = faceSheetFileInputRef.current.files[i];
        const reader = new FileReader();
        reader.onloadend = () => {
          setFaceSheetData((prvState) => ({
            activeIndex: i,
            images: prvState.images.concat(reader.result),
            croppedImages: prvState.croppedImages.concat(reader.result)
          }));
        };
        reader.readAsDataURL(img);
      }
      uploadPrivateFile(null, faceSheetFileInputRef, 'faceSheet');
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnFaceSheetCropAccept = (closeForm = true) => {
    if (closeForm) {
      setFaceSheetUploading(true);
      setShowFaceSheetCropper(false);
    }

    createPdf(faceSheetData.croppedImages, 'faceSheet');
  };
  // #endregion

  // #region Consent Form File Upload Handlers
  const handleOnConsentFormDrop = (img) => {
    setConsentFormUploading(true);
    // Creating Consent Form data object from dopped image file
    const reader = new FileReader();
    reader.onloadend = () => {
      setConsentFormData((prvState) => ({
        activeIndex: 0,
        images: prvState.images.concat(reader.result),
        croppedImages: prvState.croppedImages.concat(reader.result)
      }));
    };
    reader.readAsDataURL(img);
    uploadPrivateFile(img, null, 'consentForm');
  };

  const handleOnConsentFormSelected = () => {
    if (consentFormFileInputRef.current.files.length > 0) {
      setConsentFormUploading(true);
      // Creating Consent Form data object from selected image file
      for (let i = 0; i < consentFormFileInputRef.current.files.length; i++) {
        const img = consentFormFileInputRef.current.files[i];
        const reader = new FileReader();
        reader.onloadend = () => {
          setConsentFormData((prvState) => ({
            activeIndex: i,
            images: prvState.images.concat(reader.result),
            croppedImages: prvState.croppedImages.concat(reader.result)
          }));
        };
        reader.readAsDataURL(img);
      }
      uploadPrivateFile(null, consentFormFileInputRef, 'consentForm');
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnConsentFormCropAccept = () => {
    setConsentFormUploading(true);
    setShowConsentFormCropper(false);
    createPdf(consentFormData.croppedImages, 'consentForm');
  };
  // #endregion

  // #region Pre/Post Op Form File Upload Handlers
  const handleOnPrePostOpFormDrop = (img) => {
    setPrePostOpFormUploading(true);
    // Creating Pre/Post OP Form data object from dopped image file
    const reader = new FileReader();
    reader.onloadend = () => {
      setPrePostOpFormData((prvState) => ({
        activeIndex: 0,
        images: prvState.images.concat(reader.result),
        croppedImages: prvState.croppedImages.concat(reader.result)
      }));
    };
    reader.readAsDataURL(img);
    uploadPrivateFile(img, null, 'prePostOpForm');
  };

  const handleOnPrePostOpFormSelected = () => {
    if (prePostOpFormFileInputRef.current.files.length > 0) {
      setPrePostOpFormUploading(true);
      // Creating Pre/Post Op Form data object from selected image file
      for (let i = 0; i < prePostOpFormFileInputRef.current.files.length; i++) {
        const img = prePostOpFormFileInputRef.current.files[i];
        const reader = new FileReader();
        reader.onloadend = () => {
          setPrePostOpFormData((prvState) => ({
            activeIndex: i,
            images: prvState.images.concat(reader.result),
            croppedImages: prvState.croppedImages.concat(reader.result)
          }));
        };
        reader.readAsDataURL(img);
      }
      uploadPrivateFile(null, prePostOpFormFileInputRef, 'prePostOpForm');
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnPrePostOpFormCropAccept = () => {
    setPrePostOpFormUploading(true);
    setShowPrePostOpFormCropper(false);

    createPdf(prePostOpFormData.croppedImages, 'prePostOpForm');
  };
  // #endregion

  // #region Intra Op Form File Upload Handlers
  const handleOnIntraOpFormDrop = (img) => {
    setIntraOpFormUploading(true);
    // Creating IntraOp Form data object from dopped image file
    const reader = new FileReader();
    reader.onloadend = () => {
      setIntraOpFormData((prvState) => ({
        activeIndex: 0,
        images: prvState.images.concat(reader.result),
        croppedImages: prvState.croppedImages.concat(reader.result)
      }));
    };
    reader.readAsDataURL(img);
    uploadPrivateFile(img, null, 'intraOpForm');
  };

  const handleOnIntraOpFormSelected = () => {
    if (intraOpFormFileInputRef.current.files.length > 0) {
      setIntraOpFormUploading(true);
      // Creating FaceSheet data object from selected image file
      for (let i = 0; i < intraOpFormFileInputRef.current.files.length; i++) {
        const img = intraOpFormFileInputRef.current.files[i];
        const reader = new FileReader();
        reader.onloadend = () => {
          setIntraOpFormData((prvState) => ({
            activeIndex: i,
            images: prvState.images.concat(reader.result),
            croppedImages: prvState.croppedImages.concat(reader.result)
          }));
        };
        reader.readAsDataURL(img);
      }
      uploadPrivateFile(null, intraOpFormFileInputRef, 'intraOpForm');
    } else {
      console.log('No file is selected!');
    }
  };

  const handleOnIntraOpFormCropAccept = () => {
    setIntraOpFormUploading(true);
    setShowIntraOpFormCropper(false);
    createPdf(intraOpFormData.croppedImages, 'intraOpForm');
  };
  // #endregion

  const createCase = () => {
    setShowSaveCaseLoader(true);
    // --------------------------- Setting headers ---------------------------
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);
    // --------------------------- Preparing case data ---------------------------
    const formData = new FormData();
    // Face Sheet
    formData.append('faceSheetKey', faceSheetKey);
    // formData.append('isFaceSheetFormPDF', isFaceSheetPDF);
    formData.append('faceSheetData', new Blob(
      [JSON.stringify(faceSheetData)],
      { type: 'text/plain' }
    ));
    // if (!isFaceSheetPDF) {}

    // Consent Form
    formData.append('consentFormKey', consentFormKey);
    // formData.append('isConsentFormPDF', isConsentFormPDF);
    formData.append('consentFormData', new Blob(
      [JSON.stringify(consentFormData)],
      { type: 'text/plain' }
    ));
    // if (!isConsentFormPDF) {}

    // Pre/Post Op Form Data
    formData.append('prePostOpFormKey', prePostOpFormKey);
    // formData.append('isPrePostOpFormPDF', isPrePostOpFormPDF);
    formData.append('prePostOpFormData', new Blob(
      [JSON.stringify(prePostOpFormData)],
      { type: 'text/plain' }
    ));
    // if (!isPrePostOpFormPDF) {}

    // Intra Op Form Data
    formData.append('intraOpFormKey', intraOpFormKey);
    // formData.append('isIntraOpFormPDF', isIntraOpFormPDF);
    formData.append('intraOpFormData', new Blob(
      [JSON.stringify(intraOpFormData)],
      { type: 'text/plain' }
    ));
    // if (!isIntraOpFormPDF) {}

    // Surgery Date
    formData.append('dateOfSurgery', new Date(surgeryYear, surgeryMonth, surgeryDate));

    // Patient Type
    if (patientType === 'In Patient') {
      formData.append('patientType', 'InPatient');
    }
    if (patientType === 'Out Patient') {
      formData.append('patientType', 'OutPatient');
    }

    // Facility
    formData.append('facility', JSON.stringify(facilityList[0]));

    // Surgeons
    formData.append('surgeons', JSON.stringify(surgeonsList.filter(el => el.npi !== 0)));
    formData.append('isNoneSurgeon', JSON.stringify(isNoneSurgeon));
    formData.append('otherSurgeons', JSON.stringify(surgeonsList.filter(el => el.npi === 0)));

    // Anesthesiologists
    formData.append('anesthesiologists', JSON.stringify(anesthesiologistsList.filter(el => el.npi !== 0)));
    formData.append('otherAnesthesiologists', JSON.stringify(anesthesiologistsList.filter(el => el.npi === 0)));
    formData.append('isNoneAnesthesiologist', JSON.stringify(isNoneAnesthesiologist));

    // CRNAs
    formData.append('crnas', JSON.stringify(crnasList.filter(el => el.npi !== 0)));
    formData.append('otherCrnas', JSON.stringify(crnasList.filter(el => el.npi === 0)));
    formData.append('isNoneCrna', JSON.stringify(isNoneCrna));

    // Surgical Procedures
    formData.append('surgicalProcedures', JSON.stringify(proceduresList.filter(el => el.npi !== 0)));
    formData.append('otherSurgicalProcedures', JSON.stringify(proceduresList.filter(el => el.npi === 0)));

    // Pre Op Dx
    formData.append('preOpDx', JSON.stringify(preOpDxList.filter(el => el.npi !== 0)));
    formData.append('otherPreOpDx', JSON.stringify(preOpDxList.filter(el => el.npi === 0)));

    // Post Op Dx
    formData.append('isSameAsPreOpDx', isSameAsPreOpDx);
    if (!isSameAsPreOpDx) {
      formData.append('postOpDx', JSON.stringify(postOpDxList.filter(el => el.npi !== 0)));
      formData.append('otherPostOpDx', JSON.stringify(postOpDxList.filter(el => el.npi === 0)));
    } else {
      formData.append('postOpDx', JSON.stringify(preOpDxList.filter(el => el.npi !== 0)));
      formData.append('otherPostOpDx', JSON.stringify(preOpDxList.filter(el => el.npi === 0)));
    }

    // Anesthesia Procedure
    formData.append('anesthesiaProcedures', JSON.stringify(anesthesiaProceduresList.filter(el => el.npi !== 0)));
    formData.append('otherAnesthesiaProcedures', JSON.stringify(anesthesiaProceduresList.filter(el => el.npi === 0)));
    formData.append('isNoneAnesthesiaProcedure', JSON.stringify(isNoneAnesthesiaProcedure));

    // Anesthesia Type
    const anesthesiaTypes = [];
    if (isGaEtt) {
      anesthesiaTypes.push('GA-ETT');
    }
    if (isGaLma) {
      anesthesiaTypes.push('GA-LMA');
    }
    if (isMac) {
      anesthesiaTypes.push('MAC');
    }
    if (isEpidural) {
      anesthesiaTypes.push('Epidural');
    }
    if (isSab) {
      anesthesiaTypes.push('SAB');
    }
    if (isRegional) {
      anesthesiaTypes.push('Regional');
    }
    if (isLocal) {
      anesthesiaTypes.push('Local');
    }
    if (isOther) {
      anesthesiaTypes.push('Other');
    }

    formData.append('anesthesiaTypes', JSON.stringify(anesthesiaTypes));

    // Anesthesia Start Time
    // const anesthesiaStartDateTime = new Date(anesthesiaStartYear, anesthesiaStartMonth, anesthesiaStartDate, anesthesiaStartHour, anesthesiaStartMinute);
    // formData.append('anesthesiaStartDate', anesthesiaStartDateTime);
    formData.append('anesthesiaStartDate', `${anesthesiaStartYear}-${padZero(anesthesiaStartMonth + 1)}-${padZero(anesthesiaStartDate)}` + 'T00:00:00.000Z');
    formData.append('anesthesiaStartHour', anesthesiaStartHour);
    formData.append('anesthesiaStartMinute', anesthesiaStartMinute);

    // Anesthesia End Time
    // const anesthesiaEndDateTime = new Date(anesthesiaEndYear, anesthesiaEndMonth, anesthesiaEndDate, anesthesiaEndHour, anesthesiaEndMinute);
    // formData.append('anesthesiaEndDate', anesthesiaEndDateTime);
    formData.append('anesthesiaEndDate', `${anesthesiaEndYear}-${padZero(anesthesiaEndMonth + 1)}-${padZero(anesthesiaEndDate)}` + 'T00:00:00.000Z');
    formData.append('anesthesiaEndHour', anesthesiaEndHour);
    formData.append('anesthesiaEndMinute', anesthesiaEndMinute);

    // ASA Score
    formData.append('asaScore', asaScore);
    formData.append('isEmergencyAsaScore', isEmergencyAsaScore);

    // Position
    const anesthesiaPositions = [];
    if (isSupine) {
      anesthesiaPositions.push('supine');
    }
    if (isProne) {
      anesthesiaPositions.push('prone');
    }
    if (isLithotomy) {
      anesthesiaPositions.push('lithotomy');
    }
    if (isLateralLeft) {
      anesthesiaPositions.push('lateralLeft');
    }
    if (isLateralRight) {
      anesthesiaPositions.push('lateralRight');
    }
    if (isSitting) {
      anesthesiaPositions.push('sitting');
    }
    formData.append('anesthesiaPositions', JSON.stringify(anesthesiaPositions));

    // Modifiers
    const anesthesiaModifiersArr = [];
    if (isFieldAvoidance) {
      anesthesiaModifiersArr.push('FieldAvoidance');
    }
    if (isControlledHypotension) {
      anesthesiaModifiersArr.push('ControlledHypotension');
    }
    if (isControlledHypothermia) {
      anesthesiaModifiersArr.push('ControlledHypothermia');
    }
    if (isExtremeAge) {
      anesthesiaModifiersArr.push('Extreme Age');
    }
    if (isPronePosition) {
      anesthesiaModifiersArr.push('Prone Position');
    }
    formData.append('anesthesiaModifiers', JSON.stringify(anesthesiaModifiersArr));

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/cases/createCase`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        setShowSaveCaseLoader(false);
        if (res.success) {
          props.reloadData();
        }
      });
  };

  const addFavAnesthesiaProcedure = (procedure) => {
    setFavAnesthesiaProcedures(prvState => prvState.concat(procedure));

    const usr = JSON.parse(localStorage.getItem('usr'));
    const formData = new FormData();
    formData.append('userId', usr._id);
    formData.append('procedureId', procedure._id);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/users/addAnesthesiaProcedureForUser`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
      })
      .catch(error => console.log('error', error));
  };

  const removeFavAnesthesiaProcedure = (procedureId) => {
    const updatedFavAnesthesiaProcedures = favAnesthesiaProcedures.filter(el => el._id !== procedureId);
    setFavAnesthesiaProcedures(updatedFavAnesthesiaProcedures);

    const usr = JSON.parse(localStorage.getItem('usr'));
    const formData = new FormData();
    formData.append('userId', usr._id);
    formData.append('procedureId', procedureId);

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow'
    };

    fetch(`${BASE_URL}/users/removeAnesthesiaProcedureForUser`, requestOptions)
      .then(response => response.text())
      .then(result => {
        const res = JSON.parse(result);
        console.log(res);
      })
      .catch(error => console.log('error', error));
  };

  const getFavAnesthesiaProcedures = () => {
    const usr = JSON.parse(localStorage.getItem('usr'));

    const url = new URL(`${BASE_URL}/users/getAnesthesiaProceduresForUser`);
    const params = { userId: usr._id };
    url.search = new URLSearchParams(params).toString();
    fetch(url)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        console.log(data.procedures);
        setFavAnesthesiaProcedures(data.procedures);
      })
      .catch(error => console.log('error', error));
  };

  const getAllAnesthesiaProcedures = () => {
    debouncedAnesthesiaProceduresSearch('');
  };

  // #region Search Menus Items
  const facilitySearchItems = [];
  if (facilityValue.length > 0) {
    facilitySearchItems.push(
      <div
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        key='11111Fac'
        onClick={() => {
          setShowFacilitySearchMenu(false);
          setFacilityValue('');
          setFacilityList((prevState) => {
            return prevState.concat({
              id: Math.random().toString(36).slice(2),
              facility: '',
              facilityName: facilityValue,
              npi: 0,
              text: facilityValue,
              icon: 'https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg',
              fullName: facilityValue,
              caseRelation: ''
            });
          });
        }}
      >
        {/* Avatar */}
        <div className={css(styles.menuItemAvatarContainer)}>
          <img
            src='https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg'
            alt='Facility Avatar'
            className={css(styles.menuItemAvatarImg)}
          />
        </div>
        {/* Facility Name */}
        <div className='ml-2'>
          <div>
            {facilityValue}
          </div>
        </div>
      </div>
    );
  }
  if (facilitySearchResults.length > 0) {
    facilitySearchResults.forEach((el) => {
      facilitySearchItems.push(
        <div
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          key={el._id}
          onClick={() => {
            setShowFacilitySearchMenu(false);
            setFacilityValue('');
            setFacilityList((prevState) => {
              return prevState.concat({
                id: el._id,
                facility: el._id,
                facilityName: el.facilityName,
                npi: el.npi,
                text: `${el.facilityName}`,
                icon: el.avatarImageUrl,
                user: el._id,
                fullName: el.facilityName,
                caseRelation: el.taxonomy
              });
            });
          }}
        >
          {/* Avatar */}
          <div className={css(styles.menuItemAvatarContainer)}>
            <img
              src={el.avatarImageUrl}
              alt='Facility Avatar'
              className={css(styles.menuItemAvatarImg)}
            />
          </div>
          {/* Facility Name */}
          <div className='ml-2'>
            <div>{getHighlightedText(el.facilityName, facilityValue)}</div>
            <div>{el.taxonomy}</div>
          </div>
        </div>
      );
    });
  }

  const surgeonsSearchItems = [];
  if (surgeonValue.length > 0) {
    surgeonsSearchItems.push(
      <div
        key='22222Surg'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        onClick={(e) => {
          e.stopPropagation();
          setShowSurgeonsSearchMenu(false);
          setSurgeonValue('');
          setSurgeonsList((prevState) => {
            return prevState.concat({
              id: Math.random().toString(36).slice(2),
              text: surgeonValue,
              icon: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
              user: '',
              fullName: surgeonValue,
              npi: 0,
              caseRelation: 'Surgeon'
            });
          });
        }}
      >
        {/* Avatar */}
        <div className={css(styles.menuItemAvatarContainer)}>
          <img
            src='https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg'
            alt='Surgeon Avatar'
            className={css(styles.menuItemAvatarImg)}
          />
        </div>
        {/* Surgeon Name && Specialty */}
        <div className='ml-2'>
          <div>{surgeonValue}</div>
        </div>
      </div>
    );
  }
  if (surgeonsSearchResults.length > 0) {
    surgeonsSearchResults.forEach((el, idx) => {
      console.log('Sugeons search ---> menu items');
      console.log(el);
      surgeonsSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            setShowSurgeonsSearchMenu(false);
            setSurgeonValue('');
            setSurgeonsList((prevState) => {
              if (prevState.findIndex(item => item.id === el._id) < 0) {
                return prevState.concat({
                  id: el._id,
                  text: `${el.lastName}, ${el.firstName}`,
                  icon: el.avatarImageUrl,
                  user: el._id,
                  fullName: `${el.lastName}, ${el.firstName}`,
                  npi: el.npi,
                  caseRelation: el.taxonomy.desc || 'Surgeon'
                });
              } else {
                return prevState;
              }
            });
          }}
        >
          {/* Avatar */}
          <div className={css(styles.menuItemAvatarContainer)}>
            <img
              src={el.avatarImageUrl}
              alt='Surgeon Avatar'
              className={css(styles.menuItemAvatarImg)}
            />
          </div>
          {/* Surgeon Name && Specialty */}
          <div className='ml-2'>
            <div>{getHighlightedText(`${el.lastName}, ${el.firstName}`, surgeonValue)}</div>
            <div>{el.taxonomy.desc}</div>
          </div>
        </div>
      );
    });
  }

  const anesthesiologistsSearchItems = [];
  if (anesthesiologistValue.length > 0) {
    anesthesiologistsSearchItems.push(
      <div
        key='22222Anesthesiologist'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        onClick={(e) => {
          e.stopPropagation();
          setShowAnesthesiologistsSearchMenu(false);
          setAnesthesiologistValue('');
          setAnesthesiologistsList((prevState) => {
            return prevState.concat({
              id: Math.random().toString(36).slice(2),
              text: anesthesiologistValue,
              icon: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
              user: '',
              fullName: anesthesiologistValue,
              npi: 0,
              caseRelation: 'Anesthesiologist'
            });
          });
        }}
      >
        {/* Avatar */}
        <div className={css(styles.menuItemAvatarContainer)}>
          <img
            src='https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg'
            alt='Anesthesiologist Avatar'
            className={css(styles.menuItemAvatarImg)}
          />
        </div>
        {/* Anesthesiologist Name && Specialty */}
        <div className='ml-2'>
          <div>
            {anesthesiologistValue}
          </div>
        </div>
      </div>
    );
  }
  if (anesthesiologistsSearchResults.length > 0) {
    anesthesiologistsSearchResults.forEach((el) => {
      anesthesiologistsSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            setShowAnesthesiologistsSearchMenu(false);
            setAnesthesiologistValue('');
            setAnesthesiologistsList((prevState) => {
              if (prevState.findIndex(item => item.id === el._id) < 0) {
                return prevState.concat({
                  id: el._id,
                  text: `${el.displayName}`,
                  icon: el.avatarImageUrl,
                  user: el._id,
                  fullName: `${el.lastName}, ${el.firstName}`,
                  npi: el.npi,
                  caseRelation: 'Anesthesiologist'
                });
              } else {
                return prevState;
              }
            });
          }}
        >
          {/* Avatar */}
          <div className={css(styles.menuItemAvatarContainer)}>
            <img
              src={el.avatarImageUrl}
              alt='Anesthesiologist Avatar'
              className={css(styles.menuItemAvatarImg)}
            />
          </div>
          {/* Anesthesiologist Name && Specialty */}
          <div className='ml-2'>
            <div>{getHighlightedText(`${el.lastName}, ${el.firstName}`, anesthesiologistValue)}</div>
            <div>{el.specialty}</div>
          </div>
        </div>
      );
    });
  }

  const crnasSearchItems = [];
  if (crnaValue.length > 0) {
    crnasSearchItems.push(
      <div
        key='3434343Crna'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        onClick={(e) => {
          e.stopPropagation();
          setShowCrnasSearchMenu(false);
          setCrnaValue('');
          setCrnasList((prevState) => {
            return prevState.concat({
              id: Math.random().toString(36).slice(2),
              text: crnaValue,
              icon: 'https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg',
              user: '',
              fullName: crnaValue,
              npi: 0,
              caseRelation: 'CRNA'
            });
          });
        }}
      >
        {/* Avatar */}
        <div className={css(styles.menuItemAvatarContainer)}>
          <img
            src='https://quantumtrader.s3.amazonaws.com/default-user-avatar.svg'
            alt='Anesthesiologist Avatar'
            className={css(styles.menuItemAvatarImg)}
          />
        </div>
        {/* CRNA Name & Specialty */}
        <div className='ml-2'>
          <div>
            {crnaValue}
          </div>
        </div>
      </div>
    );
  }
  if (crnasSearchResults.length > 0) {
    crnasSearchResults.forEach((el) => {
      crnasSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            setShowCrnasSearchMenu(false);
            setCrnaValue('');
            setCrnasList((prevState) => {
              if (prevState.findIndex(item => item.id === el._id) < 0) {
                return prevState.concat({
                  id: el._id,
                  text: `${el.displayName}`,
                  icon: el.avatarImageUrl,
                  user: el._id,
                  fullName: `${el.lastName}, ${el.firstName}`,
                  npi: el.npi,
                  caseRelation: 'CRNA'
                });
              } else {
                return prevState;
              }
            });
          }}
        >
          {/* Avatar */}
          <div className={css(styles.menuItemAvatarContainer)}>
            <img
              src={el.avatarImageUrl}
              alt='Anesthesiologist Avatar'
              className={css(styles.menuItemAvatarImg)}
            />
          </div>
          {/* CRNA Name & Specialty */}
          <div className='ml-2'>
            <div>{getHighlightedText(`${el.lastName}, ${el.firstName}`, crnaValue)}</div>
            <div>{el.specialty}</div>
            {/* <div>{`${el.lastName}, ${el.firstName}`}</div>
              <div>{el.specialty}</div> */}
          </div>
        </div>
      );
    });
  }

  const surgicalProceduresSearchItems = [];
  if (procedureValue.length > 0) {
    surgicalProceduresSearchItems.push(
      <div
        key='88888999SurgProcedure'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        onClick={(e) => {
          e.stopPropagation();
          setShowSurgicalProceduresSearchMenu(false);
          setProcedureValue('');
          setProceduresList((prevState) => {
            return prevState.concat({
              id: Math.random().toString(36).slice(2),
              text: procedureValue,
              code: '',
              description: procedureValue,
              npi: 0
            });
          });
        }}
      >
        <div className='ml-2'>
          <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
            {procedureValue}
          </div>
        </div>
      </div>
    );
  }
  if (surgicalProceduresSearchResults.length > 0) {
    surgicalProceduresSearchResults.forEach((el) => {
      surgicalProceduresSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            setShowSurgicalProceduresSearchMenu(false);
            setProcedureValue('');
            setProceduresList((prevState) => {
              if (prevState.findIndex(item => item.id === el._id) < 0) {
                return prevState.concat({
                  id: el._id,
                  text: `${el.shortDescription}`,
                  code: el.code,
                  description: `${el.shortDescription}`,
                  npi: el.npi
                });
              } else {
                return prevState;
              }
            });
          }}
        >
          <div className='ml-2'>
            <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
              {el.shortDescription}
            </div>
          </div>
        </div>
      );
    });
  }

  const preOpDxSearchItems = [];
  if (preOpDxValue.length > 0) {
    preOpDxSearchItems.push(
      <div
        key='877891781preOp'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        style={{ height: '40px' }}
        onClick={(e) => {
          e.stopPropagation();
          setShowPreOpDxSearchMenu(false);
          setPreOpDxValue('');
          setPreOpDxList((prevState) => {
            return prevState.concat({
              id: Math.random().toString(36).slice(2),
              code: '',
              description: preOpDxValue,
              npi: 0
            });
          });
        }}
      >
        <div className='ml-2'>
          <div>{preOpDxValue}</div>
        </div>
      </div>
    );
  }
  if (preOpDxSearchResults.length > 0) {
    preOpDxSearchResults.forEach((el) => {
      preOpDxSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            setShowPreOpDxSearchMenu(false);
            setPreOpDxValue('');
            setPreOpDxList((prevState) => {
              if (prevState.findIndex(item => item.id === el._id) < 0) {
                return prevState.concat({
                  id: el._id,
                  code: el.fullCode,
                  description: el.abbreviatedDescription,
                  npi: el.npi
                });
              } else {
                return prevState;
              }
            });
          }}
        >
          <div className='ml-2'>
            <div>{el.fullCode}</div>
            {/* <div>{getHighlightedText(el.abbreviatedDescription, highlight)}</div> */}
            <div>{el.abbreviatedDescription}</div>
          </div>
        </div>
      );
    });
  }

  const postOpDxSearchItems = [];
  if (postOpDxValue.length > 0) {
    postOpDxSearchItems.push(
      <div
        key='908098098PostOpDx'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        style={{ height: '40px' }}
        onClick={(e) => {
          e.stopPropagation();
          setShowPostOpDxSearchMenu(false);
          setPostOpDxValue('');
          setPostOpDxList((prevState) => {
            return prevState.concat({
              id: Math.random().toString(36).slice(2),
              code: '',
              description: postOpDxValue,
              npi: 0
            });
          });
        }}
      >
        <div className='ml-2'>
          <div>{postOpDxValue}</div>
        </div>
      </div>
    );
  }
  if (postOpDxSearchResults.length > 0) {
    postOpDxSearchResults.forEach((el) => {
      postOpDxSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
          onClick={(e) => {
            e.stopPropagation();
            setShowPostOpDxSearchMenu(false);
            setPostOpDxValue('');
            setPostOpDxList((prevState) => {
              if (prevState.findIndex(item => item.id === el._id) < 0) {
                return prevState.concat({
                  id: el._id,
                  code: el.fullCode,
                  description: el.abbreviatedDescription,
                  npi: el.npi
                });
              } else {
                return prevState;
              }
            });
          }}
        >
          <div className='ml-2'>
            <div>{el.fullCode}</div>
            {/* <div>{getHighlightedText(el.abbreviatedDescription, highlight)}</div> */}
            <div>{el.abbreviatedDescription}</div>
          </div>
        </div>
      );
    });
  }

  const anesthesiaProceduresSearchItems = [];
  if (anesthesiaProcedureValue.length > 0) {
    anesthesiaProceduresSearchItems.push(
      <div
        key='989898AnesProcedure'
        className={`fx fx__v-center ${css(styles.menuItem)}`}
        onClick={(e) => {
          e.stopPropagation();
          setShowAnesthesiaProceduresSearchMenu(false);
          setAnesthesiaProcedureValue('');
          setAnesthesiaProceduresList((prevState) => {
            return prevState.concat({
              id: Math.random().toString(36).slice(2),
              text: anesthesiaProcedureValue,
              code: '',
              description: anesthesiaProcedureValue,
              npi: 0
            });
          });
        }}
      >
        <div
          style={{
            height: '40px',
            display: 'flex',
            alignItems: 'center'
          }}
          className='ml-2'
        >
          <div>{`?Procedure: "${anesthesiaProcedureValue}"`}</div>
        </div>
      </div>
    );
  }
  if (anesthesiaProceduresSearchResults.length > 0) {
    anesthesiaProceduresSearchResults.forEach((el) => {
      const isFav = favAnesthesiaProcedures.findIndex(proc => proc._id === el._id) >= 0;
      anesthesiaProceduresSearchItems.push(
        <div
          key={el._id}
          className={`fx fx__v-center ${css(styles.menuItem)}`}
        >
          <div
            className='ml-2'
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                flexGrow: '1'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowAnesthesiaProceduresSearchMenu(false);
                setAnesthesiaProcedureValue('');
                setAnesthesiaProceduresList((prevState) => {
                  if (prevState.findIndex(item => item.id === el._id) < 0) {
                    return prevState.concat({
                      id: el._id,
                      text: `${el.description}`,
                      code: '',
                      description: `${el.description}`,
                      npi: 1
                    });
                  } else {
                    return prevState;
                  }
                });
              }}
            >
              {getHighlightedText(el.description, anesthesiaProcedureValue)}
            </div>
            <div className={css(styles.menuItemStarContainer, isFav && styles.isFav)}>
              <i
                className='fa fa-star'
                onClick={() => {
                  if (!isFav) {
                    addFavAnesthesiaProcedure(el);
                  } else {
                    removeFavAnesthesiaProcedure(el._id);
                  }
                }}
              />
            </div>
          </div>
        </div>
      );
    });
  }
  // #endregion

  return (
    <Drawer
      placement='right'
      visible={true}
      toggleDrawer={() => props.onClose(true)}
      zIndex="2000"
      width={drawerWidth}
    >
      {/* Save/Create case loader */}
      {showSaveCaseLoader &&
        <div style={{ height: '100%', background: 'black' }}>
          <Loader iconType="cog" iconSize="5x" text="Creating case" />
        </div>
      }
      {/* ----------- Face Sheet Camera and Cropper Containers ----------- */}
      {!isFaceSheetUploading && isTouchDevice && showFaceSheetCamera &&
        <CameraContainer
          images={faceSheetData.images}
          title='Face Sheet'
          isReScan={faceSheetData.isReScan}
          activeIndex={faceSheetData.activeIndex}
          onSkip={() => { setShowFaceSheetCamera(false); }}
          onCancel={() => {
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(false);
          }}
          onEdit={() => {
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(true);
          }}
          onScan={(img) => {
            if (faceSheetData.isReScan) {
              setFaceSheetData((prvState) => {
                const tmpImages = prvState.images.concat();
                tmpImages[prvState.activeIndex] = img;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = img;
                return {
                  ...prvState,
                  images: tmpImages,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setFaceSheetData((prvState) => ({
                ...prvState,
                activeIndex: prvState.images.length,
                images: prvState.images.concat(img),
                croppedImages: prvState.croppedImages.concat(img)
              }));
            }
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(true);
          }}
        />
      }
      {/* !isFaceSheetUploading && isTouchDevice && showFaceSheetCropper && */}
      {!isFaceSheetUploading && showFaceSheetCropper &&
        <CropperContainer
          title='Face Sheet'
          ref={faceSheetCropperRef}
          keySuffix='img_face_sheet'
          editMode={faceSheetCropperEditMode}
          onModeChange={(mode) => {
            if (mode === 'edit') {
              setLastFaceSheetData(clone(faceSheetData));
            }
            setFaceSheetCropperEditMode(mode === 'edit');
          }}
          images={faceSheetData.images}
          croppedImages={faceSheetData.croppedImages}
          currentIndex={Math.min(faceSheetData.activeIndex, faceSheetData.images.length - 1)}
          onPrint={() => handleOnClickViewFile(faceSheetKey)}
          onDownload={() => handleOnClickViewFile(faceSheetKey)}
          loadImage={(idx) => {
            setFaceSheetData(prvState => ({ ...prvState, activeIndex: idx }));
          }}
          onDelete={(idx) => {
            setFaceSheetData((prvState) => {
              const images = prvState.images.concat();
              images.splice(idx, 1);
              const croppedImages = prvState.croppedImages.concat();
              croppedImages.splice(idx, 1);
              return {
                ...prvState,
                images,
                croppedImages,
                // activeIndex: Math.max(images.length - 1, 0)
                activeIndex: Math.max(prvState.activeIndex - 1, 0)
              };
            });
          }}
          onRevert={(idx) => {
            const tmpCroppedImages = faceSheetData.croppedImages.concat();
            tmpCroppedImages[idx] = faceSheetData.images[idx];
            setFaceSheetData((prvData) => ({
              ...prvData,
              croppedImages: tmpCroppedImages
            }));
          }}
          onClose={(deleteMode) => {
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(false);
            if (deleteMode !== 'delete') {
              handleOnFaceSheetCropAccept();
            }
            // if (deleteMode === 'update') {
            //   if (faceSheetKey !== '') {
            //     createPdf(faceSheetData.croppedImages, 'faceSheet');
            //   }
            // } else if (deleteMode === 'delete') {
            //   console.log('Do nothing ...');
            // } else {
            //   setFaceSheetData(clone(lastFaceSheetData));
            // }
          }}
          onCancel={() => {
            setShowFaceSheetCamera(false);
            setShowFaceSheetCropper(false);
          }}
          onReScan={() => {
            setFaceSheetData(prvState => ({ ...prvState, isReScan: true }));
            setShowFaceSheetCamera(true);
          }}
          onScanNext={() => {
            setShowFaceSheetCamera(true);
            setShowFaceSheetCropper(false);
            setFaceSheetData(prvState => ({
              ...prvState,
              activeIndex: prvState.activeIndex + 1,
              isReScan: false
            }));
          }}
          onCrop={() => {
            if (faceSheetData.isReScan) {
              setFaceSheetData(prvState => {
                const { cropper } = faceSheetCropperRef.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                if (cropper && cropper.getCroppedCanvas && cropper.getCroppedCanvas()) {
                  tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                  return {
                    ...prvState,
                    croppedImages: tmpCroppedImages
                  };
                } else {
                  return prvState;
                }
              });
            } else {
              setFaceSheetData(prvState => {
                const { cropper } = faceSheetCropperRef.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                if (cropper && cropper.getCroppedCanvas && cropper.getCroppedCanvas()) {
                  tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                  return {
                    ...prvState,
                    croppedImages: tmpCroppedImages
                    // croppedImages: prvState.croppedImages.concat(cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0))
                  };
                } else {
                  return prvState;
                }
              });
            }
          }}
          onFinish={() => {
            handleOnFaceSheetCropAccept();
          }}
        />
      }

      {/* ----------- Consent Form Camera and Cropper Containers ----------- */}
      {!isConsentFormUploading && isTouchDevice && showConsentFormCamera &&
        <CameraContainer
          images={consentFormData.images}
          title='Consent Form'
          isReScan={consentFormData.isReScan}
          activeIndex={consentFormData.activeIndex}
          onSkip={() => { setShowConsentFormCamera(false); }}
          onCancel={() => {
            setShowConsentFormCamera(false);
            setShowConsentFormCropper(false);
          }}
          onEdit={() => {
            setShowConsentFormCamera(false);
            setShowConsentFormCropper(true);
          }}
          onScan={(img) => {
            if (consentFormData.isReScan) {
              setConsentFormData((prvState) => {
                const tmpImages = prvState.images.concat();
                tmpImages[prvState.activeIndex] = img;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = img;
                return {
                  ...prvState,
                  images: tmpImages,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setConsentFormData((prvState) => ({
                ...prvState,
                activeIndex: prvState.images.length,
                images: prvState.images.concat(img),
                croppedImages: prvState.croppedImages.concat(img)
              }));
            }
            setShowConsentFormCamera(false);
            setShowConsentFormCropper(true);
          }}
        />
      }
      {/* !isConsentFormUploading && isTouchDevice && showConsentFormCropper && */}
      {!isConsentFormUploading && showConsentFormCropper &&
        <CropperContainer
          title='Consent Form'
          ref={consentFormCropperRef}
          keySuffix='img_consent_form'
          editMode={consentFormCropperEditMode}
          onModeChange={(mode) => {
            if (mode === 'edit') {
              setLastConsentFormData(clone(consentFormData));
            }
            setConsentFormCropperEditMode(mode === 'edit');
          }}
          images={consentFormData.images}
          croppedImages={consentFormData.croppedImages}
          currentIndex={Math.min(consentFormData.activeIndex, consentFormData.images.length - 1)}
          onPrint={() => handleOnClickViewFile(consentFormKey)}
          onDownload={() => handleOnClickViewFile(consentFormKey)}
          loadImage={(idx) => {
            setConsentFormData(prvState => ({ ...prvState, activeIndex: idx }));
          }}
          onDelete={(idx) => {
            setConsentFormData((prvState) => {
              const images = prvState.images.concat();
              images.splice(idx, 1);
              const croppedImages = prvState.croppedImages.concat();
              croppedImages.splice(idx, 1);
              return {
                ...prvState,
                images,
                croppedImages,
                // activeIndex: Math.max(images.length - 1, 0)
                activeIndex: Math.max(prvState.activeIndex - 1, 0)
              };
            });
          }}
          onRevert={(idx) => {
            const tmpCroppedImages = consentFormData.croppedImages.concat();
            tmpCroppedImages[idx] = consentFormData.images[idx];
            setConsentFormData((prvData) => ({
              ...prvData,
              croppedImages: tmpCroppedImages
            }));
          }}
          onClose={(deleteMode) => {
            setShowConsentFormCamera(false);
            setShowConsentFormCropper(false);
            if (deleteMode !== 'delete') {
              handleOnConsentFormCropAccept();
            }
          }}
          onCancel={() => {
            setShowConsentFormCamera(false);
            setShowConsentFormCropper(false);
          }}
          onReScan={() => {
            setConsentFormData(prvState => ({ ...prvState, isReScan: true }));
            setShowConsentFormCamera(true);
          }}
          onScanNext={() => {
            setShowConsentFormCamera(true);
            setShowConsentFormCropper(false);
            setConsentFormData(prvState => ({
              ...prvState,
              activeIndex: prvState.activeIndex + 1,
              isReScan: false
            }));
          }}
          onCrop={() => {
            if (consentFormData.isReScan) {
              setConsentFormData(prvState => {
                const { cropper } = consentFormCropperRef.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                if (cropper && cropper.getCroppedCanvas && cropper.getCroppedCanvas()) {
                  tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                  return {
                    ...prvState,
                    croppedImages: tmpCroppedImages
                  };
                } else {
                  return prvState;
                }
              });
            } else {
              setConsentFormData(prvState => {
                const { cropper } = consentFormCropperRef.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                if (cropper && cropper.getCroppedCanvas && cropper.getCroppedCanvas()) {
                  tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                  return {
                    ...prvState,
                    croppedImages: tmpCroppedImages
                  };
                } else {
                  return prvState;
                }
              });
            }
          }}
          onFinish={() => {
            handleOnConsentFormCropAccept();
          }}
        />
      }

      {/* ----------- Pre/Post Op Form Camera and Cropper Containers ----------- */}
      {!isPrePostOpFormUploading && isTouchDevice && showPrePostOpFormCamera &&
        <CameraContainer
          images={prePostOpFormData.images}
          title='Pre/Post Op Forms'
          isReScan={prePostOpFormData.isReScan}
          activeIndex={prePostOpFormData.activeIndex}
          onSkip={() => { setShowPrePostOpFormCamera(false); }}
          onCancel={() => {
            setShowPrePostOpFormCamera(false);
            setShowPrePostOpFormCropper(false);
          }}
          onEdit={() => {
            setShowPrePostOpFormCamera(false);
            setShowPrePostOpFormCropper(true);
          }}
          onScan={(img) => {
            if (prePostOpFormData.isReScan) {
              setPrePostOpFormData((prvState) => {
                const tmpImages = prvState.images.concat();
                tmpImages[prvState.activeIndex] = img;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = img;
                return {
                  ...prvState,
                  images: tmpImages,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setPrePostOpFormData((prvState) => ({
                ...prvState,
                activeIndex: prvState.images.length,
                images: prvState.images.concat(img),
                croppedImages: prvState.croppedImages.concat(img)
              }));
            }
            setShowPrePostOpFormCamera(false);
            setShowPrePostOpFormCropper(true);
          }}
        />
      }
      {/* !isPrePostOpFormUploading && isTouchDevice && showPrePostOpFormCropper && */}
      {!isPrePostOpFormUploading && showPrePostOpFormCropper &&
        <CropperContainer
          title='Pre/Post Op Form'
          ref={prePostOpFormCropperRef}
          keySuffix='img_prePostOp_form'
          editMode={prePostOpFormCropperEditMode}
          onModeChange={(mode) => {
            if (mode === 'edit') {
              setLastPrePostOpFormDataData(clone(prePostOpFormData));
            }
            setPrePostOpFormCropperEditMode(mode === 'edit');
          }}
          images={prePostOpFormData.images}
          croppedImages={prePostOpFormData.croppedImages}
          currentIndex={Math.min(prePostOpFormData.activeIndex, prePostOpFormData.images.length - 1)}
          onPrint={() => handleOnClickViewFile(prePostOpFormKey)}
          onDownload={() => handleOnClickViewFile(prePostOpFormKey)}
          loadImage={(idx) => {
            setPrePostOpFormData(prvState => ({ ...prvState, activeIndex: idx }));
          }}
          onDelete={(idx) => {
            setPrePostOpFormData((prvState) => {
              const images = prvState.images.concat();
              images.splice(idx, 1);
              const croppedImages = prvState.croppedImages.concat();
              croppedImages.splice(idx, 1);
              return {
                ...prvState,
                images,
                croppedImages,
                activeIndex: Math.max(prvState.activeIndex - 1, 0)
                // activeIndex: Math.max(images.length - 1, 0)
              };
            });
          }}
          onRevert={(idx) => {
            const tmpCroppedImages = prePostOpFormData.croppedImages.concat();
            tmpCroppedImages[idx] = prePostOpFormData.images[idx];
            setConsentFormData((prvData) => ({
              ...prvData,
              croppedImages: tmpCroppedImages
            }));
          }}
          onClose={(deleteMode) => {
            setShowPrePostOpFormCamera(false);
            setShowPrePostOpFormCropper(false);
            if (deleteMode !== 'delete') {
              handleOnPrePostOpFormCropAccept();
            }
          }}
          onCancel={() => {
            setShowPrePostOpFormCamera(false);
            setShowPrePostOpFormCropper(false);
          }}
          onReScan={() => {
            setPrePostOpFormData(prvState => ({ ...prvState, isReScan: true }));
            setShowPrePostOpFormCamera(true);
          }}
          onScanNext={() => {
            setShowPrePostOpFormCamera(true);
            setShowPrePostOpFormCropper(false);
            setPrePostOpFormData(prvState => ({
              ...prvState,
              activeIndex: prvState.activeIndex + 1,
              isReScan: false
            }));
          }}
          onCrop={() => {
            if (prePostOpFormData.isReScan) {
              setPrePostOpFormData(prvState => {
                const { cropper } = prePostOpFormCropperRef.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                if (cropper && cropper.getCroppedCanvas && cropper.getCroppedCanvas()) {
                  tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                  return {
                    ...prvState,
                    croppedImages: tmpCroppedImages
                  };
                } else {
                  return prvState;
                }
              });
            } else {
              setPrePostOpFormData(prvState => {
                const { cropper } = prePostOpFormCropperRef.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                if (cropper && cropper.getCroppedCanvas && cropper.getCroppedCanvas()) {
                  tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                  return {
                    ...prvState,
                    croppedImages: tmpCroppedImages
                  };
                } else {
                  return prvState;
                }
              });
            }
          }}
          onFinish={() => {
            handleOnPrePostOpFormCropAccept();
          }}
        />
      }

      {/* ----------- Intra Op Form Camera and Cropper Containers ----------- */}
      {!isIntraOpFormUploading && isTouchDevice && showIntraOpFormCamera &&
        <CameraContainer
          images={intraOpFormData.images}
          title='IntraOp Form'
          isReScan={intraOpFormData.isReScan}
          activeIndex={intraOpFormData.activeIndex}
          onSkip={() => { setShowIntraOpFormCamera(false); }}
          onCancel={() => {
            setShowIntraOpFormCamera(false);
            setShowIntraOpFormCropper(false);
          }}
          onEdit={() => {
            setShowIntraOpFormCamera(false);
            setShowIntraOpFormCropper(true);
          }}
          onScan={(img) => {
            if (intraOpFormData.isReScan) {
              setIntraOpFormData((prvState) => {
                const tmpImages = prvState.images.concat();
                tmpImages[prvState.activeIndex] = img;
                const tmpCroppedImages = prvState.croppedImages.concat();
                tmpCroppedImages[prvState.activeIndex] = img;
                return {
                  ...prvState,
                  images: tmpImages,
                  croppedImages: tmpCroppedImages
                };
              });
            } else {
              setIntraOpFormData((prvState) => ({
                ...prvState,
                activeIndex: prvState.images.length,
                images: prvState.images.concat(img),
                croppedImages: prvState.croppedImages.concat(img)
              }));
            }
            setShowIntraOpFormCamera(false);
            setShowIntraOpFormCropper(true);
          }}
        />
      }
      {/* !isIntraOpFormUploading && isTouchDevice && showIntraOpFormCropper && */}
      {!isIntraOpFormUploading && showIntraOpFormCropper &&
        <CropperContainer
          title='Intra Op Form'
          ref={intraOpFormCropperRef}
          keySuffix='img_intra_form'
          editMode={intraOpFormCropperEditMode}
          onModeChange={(mode) => {
            if (mode === 'edit') {
              setLastIntraOpFormData(clone(intraOpFormData));
            }
            setIntraOpFormCropperEditMode(mode === 'edit');
          }}
          images={intraOpFormData.images}
          croppedImages={intraOpFormData.croppedImages}
          currentIndex={Math.min(intraOpFormData.activeIndex, intraOpFormData.images.length - 1)}
          onPrint={() => handleOnClickViewFile(intraOpFormKey)}
          onDownload={() => handleOnClickViewFile(intraOpFormKey)}
          loadImage={(idx) => {
            setIntraOpFormData(prvState => ({ ...prvState, activeIndex: idx }));
          }}
          onDelete={(idx) => {
            setIntraOpFormData((prvState) => {
              const images = prvState.images.concat();
              images.splice(idx, 1);
              const croppedImages = prvState.croppedImages.concat();
              croppedImages.splice(idx, 1);
              return {
                ...prvState,
                images,
                croppedImages,
                // activeIndex: Math.max(images.length - 1, 0)
                activeIndex: Math.max(prvState.activeIndex - 1, 0)
              };
            });
          }}
          onRevert={(idx) => {
            const tmpCroppedImages = intraOpFormData.croppedImages.concat();
            tmpCroppedImages[idx] = intraOpFormData.images[idx];
            setIntraOpFormData((prvData) => ({
              ...prvData,
              croppedImages: tmpCroppedImages
            }));
          }}
          onClose={(deleteMode) => {
            setShowIntraOpFormCamera(false);
            setShowIntraOpFormCropper(false);
            if (deleteMode !== 'delete') {
              handleOnIntraOpFormCropAccept();
            }
          }}
          onCancel={() => {
            setShowIntraOpFormCamera(false);
            setShowIntraOpFormCropper(false);
          }}
          onReScan={() => {
            setIntraOpFormData(prvState => ({ ...prvState, isReScan: true }));
            setShowIntraOpFormCamera(true);
          }}
          onScanNext={() => {
            setShowIntraOpFormCamera(true);
            setShowIntraOpFormCropper(false);
            setIntraOpFormData(prvState => ({
              ...prvState,
              activeIndex: prvState.activeIndex + 1,
              isReScan: false
            }));
          }}
          onCrop={() => {
            if (intraOpFormData.isReScan) {
              setIntraOpFormData(prvState => {
                const { cropper } = intraOpFormCropperRef.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                if (cropper && cropper.getCroppedCanvas && cropper.getCroppedCanvas()) {
                  tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                  return {
                    ...prvState,
                    croppedImages: tmpCroppedImages
                  };
                } else {
                  return prvState;
                }
              });
            } else {
              setIntraOpFormData(prvState => {
                const { cropper } = intraOpFormCropperRef.current;
                const tmpCroppedImages = prvState.croppedImages.concat();
                if (cropper && cropper.getCroppedCanvas && cropper.getCroppedCanvas()) {
                  tmpCroppedImages[prvState.activeIndex] = cropper.getCroppedCanvas().toDataURL('image/jpeg', 1.0);
                  return {
                    ...prvState,
                    croppedImages: tmpCroppedImages
                  };
                } else {
                  return prvState;
                }
              });
            }
          }}
          onFinish={() => {
            handleOnIntraOpFormCropAccept();
          }}
        />
      }

      {!showFaceSheetCropper && !showFaceSheetCamera && !showConsentFormCropper && !showConsentFormCamera &&
        !showPrePostOpFormCropper && !showPrePostOpFormCamera && !showIntraOpFormCropper && !showIntraOpFormCamera &&
        <div
          className={css(styles.drawerContainer)}
          style={{ overflowY: 'hidden' }}
          onScroll={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('Drawer container scrolling ...');
          }}
        >
          {/* Header */}
          <div className={css(styles.drawerHeader)}>
            <div className='fx'>
              <svg
                width="24"
                height="23"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M21.6 4.8h-4.8V2.4c0-1.332-1.068-2.4-2.4-2.4H9.6a2.392 2.392 0 0 0-2.4 2.4v2.4H2.4A2.382 2.382 0 0 0 .012 7.2L0 20.4c0 1.332 1.068 2.4 2.4 2.4h19.2c1.332 0 2.4-1.068 2.4-2.4V7.2c0-1.332-1.068-2.4-2.4-2.4Zm-7.198 0h-4.8V2.4h4.8v2.4ZM10.65 15.35h-3.3a1.35 1.35 0 1 1 0-2.7h3.3v-3.3a1.35 1.35 0 1 1 2.7 0v3.3h3.3a1.35 1.35 0 1 1 0 2.7h-3.3v3.3a1.35 1.35 0 1 1-2.7 0v-3.3Z"
                  fill="#96C8FF"
                />
              </svg>
              <div className='ml-2'>
                Create New Case
              </div>
            </div>
            <div
              className={css(styles.drawerHeaderCloseIcon)}
              onClick={() => props.onClose(true)}
            >
              <i className='fa fa-times-circle' />
            </div>
          </div>
          {/* Content Area */}
          <div
            className={css(styles.drawerContentArea)}
            ref={drawerRef}
          >
            {/* ---------- Date of surgery and Patient Type ---------- */}
            <div className={css(styles.fieldGroup)}>
              <div style={{ width: '188px' }}>
                <div className='fx fx__v-center'>
                  {showSurgeryDateAlert ? flagIcon : tickIcon}
                  <span className='ml-2'>Date of Surgery</span>
                </div>
                {/* Date of surgery */}
                <div
                  className='pl-4'
                  style={{ borderRadius: '2px', color: showSurgeryDateAlert ? salmon[500] : '' }}
                >
                  <DatePicker
                    left={5}
                    date={surgeryDate}
                    month={surgeryMonth}
                    year={surgeryYear}
                    isPrisitine={isSurgeryDatePristine}
                    color={showSurgeryDateAlert ? salmon[500] : blue[400]}
                    noUnderline
                    onChangeDate={(year, month, date) => {
                      setSurgeryDate(date);
                      setSurgeryMonth(month);
                      setSurgeryYear(year);
                      setIsSurgeryDatePristine(false);
                    }}
                  />
                </div>
              </div>
              <div ref={patientTypeMenuRef}>
                <div className='fx fx__v-center'>
                  {showPatientTypeAlert ? flagIcon : tickIcon}
                  <span className='ml-2'>Admission Type</span>
                </div>
                <div
                  onClick={() => setShowPatientTypeMenu(true)}
                  className={`pl-4 ${css(styles.patientTypeMenuHeading)}`}
                >
                  {`${patientType === '' ? 'Select' : patientType}`}
                  <i className='ml-1 fa fa-chevron-down' />
                </div>
                <PopupMenu
                  top={50}
                  right={1}
                  position='absolute'
                  parent={patientTypeMenuRef}
                  visible={showPatientTypeMenu}
                  onOutsideClick={() => setShowPatientTypeMenu(false)}
                >
                  <div className={css(styles.patientTypeMenuContainer)}>
                    {/* In Patient */}
                    <div
                      className={`pl-2 noselect ${css(styles.patientTypeMenuItem)}`}
                      onClick={() => {
                        setPatientType('In Patient');
                        setShowPatientTypeMenu(false);
                      }}
                    >
                      In Patient
                    </div>
                    {/* Out Patient */}
                    <div
                      className={`pl-2 noselect ${css(styles.patientTypeMenuItem)}`}
                      onClick={() => {
                        setPatientType('Out Patient');
                        setShowPatientTypeMenu(false);
                      }}
                    >
                      Out Patient
                    </div>
                  </div>
                </PopupMenu>
              </div>
            </div>

            {/* ---------- Anesthesia Documents ---------- */}
            <CaseField
              heading='Case Documents'
              showAlert={showFaceSheetAlert || showPrePostOpFormAlert || showIntraOpFormAlert}
              showIncomplete={false}
              className='mt-3 pl-1'
            />
            {/* ---------- Face Sheet FileCard, FileUploader & Loader ---------- */}
            {faceSheetKey !== ''
              ? <FileCard
                fileName={`Face Sheet ${isFaceSheetUploading ? '(Saving ...)' : ''}`}
                numberOfPages={faceSheetData.images.length}
                className='mt-0'
                fileKey={faceSheetKey}
                isLoading={isLoadingFaceSheetData}
                onDelete={() => {
                  setFaceSheetKey('');
                  setFaceSheetData({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
                }}
                onView={() => {
                  setFaceSheetCropperEditMode(false);
                  setFaceSheetData((prvState) => ({ ...prvState, activeIndex: 0 }));
                  setLastFaceSheetData(clone(faceSheetData));
                  setShowFaceSheetCropper(true);
                }}
                onEdit={() => {
                  setDrawerWidth(window.innerWidth);
                  setFaceSheetData((prvState) => ({ ...prvState, activeIndex: 0 }));
                  setLastFaceSheetData(clone(faceSheetData));
                  setTimeout(() => {
                    setFaceSheetCropperEditMode(true);
                    setShowFaceSheetCropper(true);
                  }, 200);
                }}
              />
              : <div className='mt-0'>
                  {isFaceSheetUploading &&
                    <div className={css(styles.loaderContainer)}>
                      <Loader
                        iconType="cog"
                        iconSize="2x"
                        text="Uploading Face Sheet"
                        style={loaderStyles}
                      />
                      {/* <i className={`fa fa-times-circle ${css(styles.loaderContainerCloseIcon)}`} /> */}
                    </div>
                  }
                  {!isFaceSheetUploading && !isTouchDevice &&
                    <FileUploader
                      title='Face Sheet'
                      titleColor={blue[400]}
                      postIcon={bellIcon}
                      text="Drag 'n Drop OR Select File"
                      showOrLineBreak={false}
                      parentRef={faceSheetFileInputRef}
                      height='100%'
                      background={gray[600]}
                      highlightBackground='rgba(0, 128, 255, 0.5)'
                      textColor={blue[400]}
                      highlightTextColor={gray[600]}
                      onDrop={handleOnFaceSheetDrop}
                      onChange={handleOnFaceSheetSelected}
                      style={fileUploaderStyles}
                    />
                  }
                  {!isFaceSheetUploading && isTouchDevice && !showFaceSheetCamera &&
                    <CameraCard
                      text='Scan Face Sheet'
                      showAlert={showFaceSheetAlert}
                      onClick={() => {
                        setShowFaceSheetCamera(true);
                        setLastFaceSheetData(clone(faceSheetData));
                      }}
                    />
                  }
                </div>
            }

            {/* ---------- Consent Form FileCard, FileUploader & Loader ---------- */}
            {consentFormKey !== ''
              ? <FileCard
                fileName={`Consent Form ${isConsentFormUploading ? ' (Saving ...)' : ''}`}
                numberOfPages={consentFormData.images.length}
                className='mt-2'
                fileKey={consentFormKey}
                isLoading={isLoadingConsentFormData}
                isTouchDevice={isTouchDevice}
                onDelete={() => {
                  setConsentFormKey('');
                  setConsentFormData({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
                }}
                onView={() => {
                  setConsentFormCropperEditMode(false);
                  setConsentFormData((prvState) => ({ ...prvState, activeIndex: 0 }));
                  setLastConsentFormData(clone(consentFormData));
                  setShowConsentFormCropper(true);
                }}
                onEdit={() => {
                  setDrawerWidth(window.innerWidth);
                  setConsentFormData((prvState) => ({ ...prvState, activeIndex: 0 }));
                  setLastConsentFormData(clone(consentFormData));
                  setTimeout(() => {
                    setConsentFormCropperEditMode(true);
                    setShowConsentFormCropper(true);
                  }, 200);
                }}
              />
              : <div className='mt-2'>
                {isConsentFormUploading &&
                  <div className={css(styles.loaderContainer)}>
                    <Loader
                      iconType="cog"
                      iconSize="2x"
                      text="Uploading Consent Form"
                      style={loaderStyles}
                    />
                    {/* <i className={`fa fa-times-circle ${css(styles.loaderContainerCloseIcon)}`} /> */}
                  </div>
                }
                {!isConsentFormUploading && !isTouchDevice &&
                  <FileUploader
                    title='Consent Form'
                    titleColor={blue[400]}
                    postIcon={bellIcon}
                    text="Drag 'n Drop OR Select File"
                    showOrLineBreak={false}
                    parentRef={consentFormFileInputRef}
                    height='100%'
                    background={gray[600]}
                    highlightBackground='rgba(0, 128, 255, 0.5)'
                    textColor={blue[400]}
                    highlightTextColor={gray[600]}
                    onDrop={handleOnConsentFormDrop}
                    onChange={handleOnConsentFormSelected}
                    style={fileUploaderStyles}
                  />
                }
                {!isConsentFormUploading && isTouchDevice && !showConsentFormCamera &&
                  <CameraCard
                    text='Scan Anesthesia Consent (If Applicable)'
                    showAlert={false /* showConsentFormAlert */}
                    onClick={() => setShowConsentFormCamera(true)}
                  />
                }
              </div>
            }
            {/* ---------- Pre/Post Op Form FileCard, FileUploader & Loader ---------- */}
            {prePostOpFormKey !== ''
              ? <FileCard
                fileName={`Pre/Post Op Form ${isPrePostOpFormUploading ? ' (Saving ...)' : ''}`}
                numberOfPages={prePostOpFormData.images.length}
                className='mt-2'
                fileKey={prePostOpFormKey}
                isLoading={isLoadingPrePostOpFormData}
                isTouchDevice={isTouchDevice}
                onDelete={() => {
                  setPrePostOpFormKey('');
                  setPrePostOpFormData({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
                }}
                onView={() => {
                  setPrePostOpFormCropperEditMode(false);
                  setPrePostOpFormData((prvState) => ({ ...prvState, activeIndex: 0 }));
                  setLastPrePostOpFormDataData(clone(prePostOpFormData));
                  setShowPrePostOpFormCropper(true);
                }}
                onEdit={() => {
                  setDrawerWidth(window.innerWidth);
                  setPrePostOpFormData((prvState) => ({ ...prvState, activeIndex: 0 }));
                  setLastPrePostOpFormDataData(clone(prePostOpFormData));
                  setTimeout(() => {
                    setPrePostOpFormCropperEditMode(true);
                    setShowPrePostOpFormCropper(true);
                  }, 200);
                }}
              />
              : <div className='mt-2'>
                {isPrePostOpFormUploading &&
                  <div className={css(styles.loaderContainer)}>
                    <Loader
                      iconType="cog"
                      iconSize="2x"
                      text="Uploading Pre/Post Form"
                      style={loaderStyles}
                    />
                    {/* <i className={`fa fa-times-circle ${css(styles.loaderContainerCloseIcon)}`} /> */}
                  </div>
                }
                {!isPrePostOpFormUploading && !isTouchDevice &&
                  <FileUploader
                    title='Pre/Post Op Form'
                    titleColor={blue[400]}
                    postIcon={bellIcon}
                    text="Drag 'n Drop OR Select File"
                    showOrLineBreak={false}
                    parentRef={prePostOpFormFileInputRef}
                    height='100%'
                    background={gray[600]}
                    highlightBackground='rgba(0, 128, 255, 0.5)'
                    textColor={blue[400]}
                    highlightTextColor={gray[600]}
                    onDrop={handleOnPrePostOpFormDrop}
                    onChange={handleOnPrePostOpFormSelected}
                    style={fileUploaderStyles}
                  />
                }
                {!isPrePostOpFormUploading && isTouchDevice && !showPrePostOpFormCamera &&
                  <CameraCard
                    text='Scan Anesthesia Pre-Op/Post-Op'
                    showAlert={showPrePostOpFormAlert}
                    onClick={() => setShowPrePostOpFormCamera(true)}
                  />
                }
              </div>
            }
            {/* ---------- Intra Op Form FileCard, FileUploader & Loader ---------- */}
            {intraOpFormKey !== ''
              ? <FileCard
                fileName={`Intr Op Form ${isIntraOpFormUploading ? ' (Saving ...)' : ''}`}
                numberOfPages={intraOpFormData.images.length}
                className='mt-2'
                fileKey={intraOpFormKey}
                isLoading={isLoadingIntraOpFormData}
                isTouchDevice={isTouchDevice}
                onDelete={() => {
                  setIntraOpFormKey('');
                  setIntraOpFormData({ images: [], croppedImages: [], activeIndex: 0, isReScan: false });
                }}
                onView={() => {
                  setIntraOpFormCropperEditMode(false);
                  setIntraOpFormData((prvState) => ({ ...prvState, activeIndex: 0 }));
                  setLastIntraOpFormData(clone(intraOpFormData));
                  setShowIntraOpFormCropper(true);
                }}
                onEdit={() => {
                  setDrawerWidth(window.innerWidth);
                  setIntraOpFormData((prvState) => ({ ...prvState, activeIndex: 0 }));
                  setLastIntraOpFormData(clone(intraOpFormData));
                  setTimeout(() => {
                    setIntraOpFormCropperEditMode(true);
                    setShowIntraOpFormCropper(true);
                  }, 200);
                }}
              />
              : <div className='mt-2'>
                {isIntraOpFormUploading &&
                  <div className={css(styles.loaderContainer)}>
                    <Loader
                      iconType="cog"
                      iconSize="2x"
                      text="Uploading Intra Form"
                      style={loaderStyles}
                    />
                    {/* <i className={`fa fa-times-circle ${css(styles.loaderContainerCloseIcon)}`} /> */}
                  </div>
                }
                {!isIntraOpFormUploading && !isTouchDevice &&
                  <FileUploader
                    title='Intra Op Form'
                    titleColor={blue[400]}
                    postIcon={bellIcon}
                    text="Drag 'n Drop OR Select File"
                    showOrLineBreak={false}
                    parentRef={intraOpFormFileInputRef}
                    height='100%'
                    background={gray[600]}
                    highlightBackground='rgba(0, 128, 255, 0.5)'
                    textColor={blue[400]}
                    highlightTextColor={gray[600]}
                    onDrop={handleOnIntraOpFormDrop}
                    onChange={handleOnIntraOpFormSelected}
                    style={fileUploaderStyles}
                  />
                }
                {!isIntraOpFormUploading && isTouchDevice && !showIntraOpFormCamera &&
                  <CameraCard
                    text='Scan Anesthesia Intra-Op'
                    showAlert={showIntraOpFormAlert}
                    onClick={() => setShowIntraOpFormCamera(true)}
                  />
                }
              </div>
            }

            {/* ---------- Facility field and cards ---------- */}
            <div ref={facilitySectionRef} style={{ height: '60px' }}>
              <CaseField
                heading='Facility'
                showAlert={showFacilityAlert}
                showIncomplete={isOtherFacility}
                showPlusIcon={false}
                className='mt-4'
              />
              {/* Facility value textbox & search menu */}
              <div
                ref={facilitySearchMenuRef}
                className='mt-0'
                style={{
                  border: `1px solid ${blue[700]}`,
                  borderRadius: '4px',
                  position: 'relative'
                }}
              >
                <TextInput
                  value={facilityValue}
                  placeholder="Add Facility"
                  style={{
                    width: `${drawerWidth - 19}px`,
                    fontSize: '14px',
                    padding: '0 4px',
                    color: blue[200],
                    borderRadius: '4px'
                  }}
                  onChange={(e) => {
                    const { value } = e.target;
                    setFacilityValue(value);
                  }}
                  onClick={() => {
                    setShowFacilitySearchMenu(true);
                  }}
                />
                {showFacilitySearchMenu &&
                  <PopupMenu
                    top={35}
                    left={-1}
                    zIndex='1550'
                    position="absolute"
                    visible={showFacilitySearchMenu}
                    parent={facilitySearchMenuRef}
                    onOutsideClick={() => { setShowFacilitySearchMenu(false); }}
                  >
                    <div className={css(styles.searchPopupMenuContainer)}>
                      {facilitySearchItems.length > 0 && facilitySearchItems}
                      {facilitySearchItems.length === 0 &&
                        <div className={css(styles.searchPopupMenuPlaceholder)}>
                          Search Facility in the text box above
                        </div>
                      }
                    </div>
                  </PopupMenu>
                }
              </div>
              {/* Facility cards */}
              {facilityList.length > 0 &&
                facilityList.map(facility => {
                  console.log(facility);
                  return (
                    <CaseUserCard
                      className='mt-1'
                      style={{ position: 'relative', top: '-37px' }}
                      avatarImageUrl={facility.icon || 'https://quantumtrader.s3.amazonaws.com/defaultAvatar.svg'}
                      showCloseIcon
                      showAlert={facility.npi === 0}
                      key={facility.id}
                      dataId={facility.id}
                      onClose={() => {
                        setFacilityList([]);
                      }}
                    >
                      <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                        <div style={{ fontSize: '16px' }}>
                          {`${facility.npi === 0 ? '?Facility:' : ''} ${facility.facilityName}`}
                        </div>
                        <div>{facility.caseRelation}</div>
                      </div>
                    </CaseUserCard>
                  );
                })
              }
            </div>

            {/* ---------- Surgeons field, cards & search menu ----------  */}
            <div ref={surgeonsSectionRef}>
              <div
                className='fx mt-4'
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <CaseField
                  heading='Surgeon(s)'
                  showAlert={showSurgeonsAlert}
                  showIncomplete={isOtherSurgeons}
                />
                {/* None - surgeon */}
                {surgeonsList.length === 0 &&
                  <div className='fx fx__v-center'>
                    <div className='crossed fx fx__hv-center'>
                      <input
                        type='checkbox'
                        className={`mr-1 ${css(styles.crossedCheckbox)}`}
                        value={isNoneSurgeon}
                        style={{ border: `1px solid ${blue[200]}` }}
                        onChange={(e) => {
                          e.stopPropagation();
                          setIsNoneSurgeon(!isNoneSurgeon);
                        }}
                      />
                    </div>
                    <span
                      style={{ color: blue[200] }}
                    >
                      None
                    </span>
                  </div>
                }
              </div>
              {/* Surgeon value textbox */}
              <div
                ref={surgeonsSearchMenuRef}
                className='mt-0'
                style={{
                  border: `1px solid ${blue[700]}`,
                  borderRadius: '4px',
                  position: 'relative',
                  display: isNoneSurgeon ? 'none' : ''
                }}
              >
                <TextInput
                  value={surgeonValue}
                  placeholder="Add Surgeon"
                  style={{
                    width: `${drawerWidth - 19}px`,
                    fontSize: '14px',
                    padding: '0 4px',
                    color: blue[200],
                    borderRadius: '4px'
                  }}
                  onChange={(e) => {
                    const { value } = e.target;
                    setSurgeonValue(value);
                  }}
                  onClick={() => setShowSurgeonsSearchMenu(true)}
                />
                {showSurgeonsSearchMenu &&
                  <PopupMenu
                    top={35}
                    left={0}
                    zIndex='1550'
                    position="absolute"
                    visible={showSurgeonsSearchMenu}
                    parent={surgeonsSearchMenuRef}
                    onOutsideClick={() => { setShowSurgeonsSearchMenu(false); }}
                  >
                    <div className={css(styles.searchPopupMenuContainer)}>
                      {surgeonsSearchItems.length > 0 && surgeonsSearchItems}
                      {surgeonsSearchItems.length === 0 &&
                        <div className={css(styles.searchPopupMenuPlaceholder)}>
                          Search Surgeon in the text box above
                        </div>
                      }
                    </div>
                  </PopupMenu>
                }
              </div>
              {/* Surgeons cards */}
              {surgeonsList.length > 0 &&
                surgeonsList.map(surgeon => {
                  console.log('-------- surgeon --------');
                  console.log(surgeon);
                  return (
                    <CaseUserCard
                      className='mt-1'
                      key={surgeon.user}
                      avatarImageUrl={surgeon.icon}
                      showCloseIcon
                      showAlert={surgeon.npi === 0}
                      dataId={surgeon.user}
                      onClose={(user) => {
                        setSurgeonsList((prevState) => {
                          return prevState.filter(el => {
                            return el.user !== user;
                          });
                        });
                      }}
                    >
                      <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                        <div>
                          {`${surgeon.npi === 0 ? '?Surgeon:' : ''} ${surgeon.fullName}`}
                        </div>
                        {surgeon.npi !== 0 && <div>{surgeon.caseRelation}</div>}
                      </div>
                    </CaseUserCard>
                  );
                })
              }
            </div>
            {/* Surgical Procedures */}
            <div ref={surgicalProceduresSectionRef}>
              <CaseField
                heading='Surgical Procedure(s)'
                showAlert={showSurgicalProceduresAlert}
                showIncomplete={isOtherSurgicalProcedures}
                plusIconLabel='CPT Code'
                className='mt-4'
              />
              {/* Surgical procedures value textbox */}
              <div
                ref={surgicalProceduresSearchMenuRef}
                className='mt-0'
                style={{
                  border: `1px solid ${blue[700]}`,
                  borderRadius: '4px',
                  position: 'relative'
                }}
              >
                <TextInput
                  value={procedureValue}
                  placeholder="Enter surgical procedure"
                  style={{
                    width: `${drawerWidth - 19}px`,
                    fontSize: '14px',
                    padding: '0 4px',
                    color: blue[200],
                    borderRadius: '4px'
                  }}
                  onChange={(e) => {
                    const { value } = e.target;
                    setProcedureValue(value);
                  }}
                  onClick={() => {
                    let surgeonsListIndex = -1;
                    for (let i = 0; i < surgeonsList.length; i++) {
                      if (surgeonsList[i].npi !== 0) {
                        surgeonsListIndex = i;
                      }
                    }

                    if (surgeonsListIndex > -1) {
                      console.log(`surgeonsListIndex = ${surgeonsListIndex}`);
                      console.log(`surgeonsList[surgeonsListIndex].npi = ${surgeonsList[surgeonsListIndex].npi}`);
                      console.log(`surgeonsList[surgeonsListIndex].user = ${surgeonsList[surgeonsListIndex].user}`);
                      console.log(surgeonsList[surgeonsListIndex]);
                      // setLastSurg(surgeonsList[surgeonsListIndex].npi);
                      getSurgicalProceduresForSurgeon(surgeonsList[surgeonsListIndex].user);
                      // setLastSurg(0);
                    } else {
                      setLastSurg(0);
                    }
                    setShowSurgicalProceduresSearchMenu(true);
                  }}
                />
                {showSurgicalProceduresSearchMenu &&
                  <PopupMenu
                    top={35}
                    left={-1}
                    zIndex='1550'
                    position="absolute"
                    visible={showSurgicalProceduresSearchMenu}
                    parent={surgicalProceduresSearchMenuRef}
                    onOutsideClick={() => { setShowSurgicalProceduresSearchMenu(false); }}
                  >
                    <div className={css(styles.searchPopupMenuContainer)}>
                      <div
                        className='fx pl-2 pr-2'
                        style={{
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: '#184878'
                        }}
                      >
                        <TabsContainer
                          dropDownId="d1"
                          showPlusIcon={false}
                          shiftMethod='availableWidth'
                          containerWidth={370}
                          otherElementsWidth={0}
                          minWidth={206}
                          dropDownTop={39}
                          dropDownLeft={44}
                          dropDownHeaderStyles={{ minWidth: '170px', maxWidth: '370px', width: '100%' }}
                          containerStyles={{ width: '100%', background: '#184878' }}
                        >
                          <TabItem
                            tabKey="1"
                            key='tabItem__all_cpt'
                            parentKey="0111"
                            text="All CPT"
                            isActive={lastSurg === 0}
                            showTabIcon={false}
                            style={{
                              ...tabItemStyles,
                              borderBottomColor: lastSurg === 0 ? blue[500] : '#184878',
                              width: '60px',
                              minWidth: '60px',
                              maxWidth: '60px'
                            }}
                            textStyles={tabItemTextStyles}
                            onActivate={() => { setLastSurg(0); }}
                          >
                            <div key='tabItem_all_cpt_key_test'>
                              All CPT
                            </div>
                          </TabItem>
                          {surgeonsList.map((el, idx) => {
                            if (el.npi !== 0) {
                              console.log(`surgicalProceduresActiveTab = ${surgicalProceduresActiveTab}`);
                              console.log(`surgicalProceduresActiveTab === (idx + 1) = ${surgicalProceduresActiveTab === (idx + 1)}`);
                              return (
                                <TabItem
                                  key={`tabItem_${el.id}`}
                                  tabKey={(idx + 2 + '')}
                                  parentKey={`01111${el._id}`}
                                  isActive={lastSurg === el.npi}
                                  showTabIcon={false}
                                  style={{
                                    ...tabItemStyles,
                                    borderBottomColor: lastSurg === el.npi ? blue[500] : '#184878',
                                    width: `${_getLastName(el.fullName).length * 10}px`,
                                    minWidth: `${_getLastName(el.fullName).length * 10}px`,
                                    maxWidth: `${_getLastName(el.fullName).length * 10}px`
                                  }}
                                  textStyles={tabItemTextStyles}
                                  onActivate={() => {
                                    // setSurgicalProceduresActiveTab(idx + 1);
                                    setLastSurg(el.npi);
                                    getSurgicalProceduresForSurgeon(el.user);
                                  }}
                                >
                                  <div key={`tabItem_${el.id}_div`}>
                                    {_getLastName(el.fullName)}
                                  </div>
                                </TabItem>
                              );
                            }
                            return null;
                          })}
                        </TabsContainer>
                        <div
                          className={`fx fx__hv-center ${css(styles.tabContainerCloseBtn)}`}
                          onClick={() => { setShowSurgicalProceduresSearchMenu(false); }}
                        >
                          <i
                            className='fa fa-times'
                            onClick={() => { setShowSurgicalProceduresSearchMenu(false); }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          height: '300px',
                          maxHeight: '300px',
                          overflowY: 'scroll',
                          padding: '8px',
                          background: blue[900]
                        }}
                      >
                        {lastSurg === 0 && surgicalProceduresSearchItems.length > 0 && surgicalProceduresSearchItems}
                        {lastSurg > 0 && surgicalProceduresForSurgeon.length > 0 &&
                          surgicalProceduresForSurgeon.map((el) => (
                            <div
                              key={el._id}
                              className={`fx fx__v-center ${css(styles.menuItem)}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowSurgicalProceduresSearchMenu(false);
                                setProcedureValue('');
                                setProceduresList((prevState) => {
                                  if (prevState.findIndex(item => item.id === el._id) < 0) {
                                    return prevState.concat({
                                      id: el._id,
                                      text: `${el.shortDescription}`,
                                      code: el.code,
                                      description: `${el.shortDescription}`,
                                      npi: 1
                                    });
                                  } else {
                                    return prevState;
                                  }
                                });
                              }}
                            >
                              <div className='ml-2'>
                                <div style={{ height: '40px', display: 'flex', alignItems: 'center' }}>
                                  {el.shortDescription}
                                </div>
                              </div>
                            </div>
                          ))
                        }
                        {lastSurg === 0 && surgicalProceduresSearchItems.length === 0 &&
                          <div className={css(styles.searchPopupMenuPlaceholder)}>
                            Search Surgical Procedure in the text box above
                          </div>
                        }
                      </div>
                    </div>
                  </PopupMenu>
                }
              </div>
              {/* Surgical Procedures cards */}
              {proceduresList.map(procedure => {
                return (
                  <CaseUserCard
                    className='mt-1'
                    avatarImageUrl={procedure.icon}
                    showCloseIcon
                    showAlert={procedure.npi === 0}
                    noAvatar
                    key={procedure.id}
                    dataId={procedure.id}
                    onClose={(id) => {
                      setProceduresList((prevState) => {
                        return prevState.filter(el => el.id !== id);
                      });
                    }}
                  >
                    <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                      <div>
                        {`${procedure.npi === 0 ? '?CPT:' : ''} ${procedure.description} ${procedure.code !== 'N/A' ? `(${procedure.code})` : ''}`}
                      </div>
                    </div>
                  </CaseUserCard>
                );
              })
              }
            </div>
            {/* PreOp Dx */}
            <div ref={preOpDxSectionRef}>
              <CaseField
                heading='PreOp Dx'
                showAlert={showPreOpDxAlert}
                showIncomplete={isOtherPreOpDx}
                plusIconLabel='ICD-10 Code'
                className='mt-4'
              />
              {/* PreOpDx value textbox */}
              <div
                ref={preOpDxSearchMenuRef}
                className='mt-0'
                style={{
                  border: `1px solid ${blue[700]}`,
                  borderRadius: '4px',
                  position: 'relative'
                }}
              >
                <TextInput
                  value={preOpDxValue}
                  placeholder="Search PreOp Dx"
                  style={{
                    width: `${drawerWidth - 19}px`,
                    fontSize: '14px',
                    padding: '0 4px',
                    color: blue[200],
                    borderRadius: '4px'
                  }}
                  onChange={(e) => {
                    const { value } = e.target;
                    setPreOpDxValue(value);
                  }}
                />
                {showPreOpDxSearchMenu &&
                  <PopupMenu
                    top={35}
                    left={0}
                    zIndex='1550'
                    position="absolute"
                    visible={showPreOpDxSearchMenu}
                    parent={preOpDxSearchMenuRef}
                    onOutsideClick={() => { setShowPreOpDxSearchMenu(false); }}
                  >
                    <div
                      style={{
                        width: '370px',
                        background: blue[900],
                        overflowY: 'auto',
                        paddingBottom: '1px',
                        borderRadius: '5px',
                        maxHeight: '200px'
                      }}
                    >
                      {preOpDxSearchItems}
                    </div>
                  </PopupMenu>
                }
              </div>
              {/* PreOp DX cards */}
              {preOpDxList.map(dx => {
                return (
                  <CaseUserCard
                    className='mt-1'
                    avatarImageUrl={dx.icon}
                    showCloseIcon
                    showAlert={dx.npi === 0}
                    noAvatar
                    key={dx.id}
                    dataId={dx.id}
                    onClose={(id) => {
                      setPreOpDxList((prevState) => {
                        return prevState.filter(el => el.id !== id);
                      });
                    }}
                  >
                    <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                      <div>
                        {`${dx.npi === 0 ? '?ICD10:' : ''} ${dx.description} ${dx.code !== 'N/A' ? `(${dx.code})` : ''}`}
                      </div>
                    </div>
                  </CaseUserCard>
                );
              })
              }
            </div>
            {/* PostOp Dx */}
            <div ref={postOpDxSectionRef}>
              <div
                className='fx mt-4'
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <CaseField
                  heading='PostOp Dx'
                  showAlert={showPostOpDxAlert}
                  showIncomplete={isOtherPostOpDx}
                  showPlusIcon={false}
                />
                {/* Same as PreOpDx */}
                {postOpDxList.length === 0 &&
                  <div className='fx fx__v-center'>
                    <Checkbox
                      text='Same as PreOp Dx'
                      isChecked={isSameAsPreOpDx}
                      onChange={() => { setIsSameAsPreOpDx(!isSameAsPreOpDx); }}
                    />
                  </div>
                }
              </div>
              {/* Post Op Dx value textbox */}
              <div
                ref={postOpDxSearchMenuRef}
                className='mt-0'
                style={{
                  border: `1px solid ${blue[700]}`,
                  borderRadius: '4px',
                  display: isSameAsPreOpDx ? 'none' : '',
                  position: 'relative'
                }}
              >
                <TextInput
                  value={postOpDxValue}
                  placeholder="Search PostOp Dx"
                  style={{
                    width: `${drawerWidth - 19}px`,
                    fontSize: '14px',
                    padding: '0 4px',
                    color: blue[200],
                    borderRadius: '4px'
                  }}
                  onChange={(e) => {
                    const { value } = e.target;
                    setPostOpDxValue(value);
                  }}
                />
                {showPostOpDxSearchMenu &&
                  <PopupMenu
                    top={35}
                    left={0}
                    zIndex='1550'
                    position="absolute"
                    visible={showPostOpDxSearchMenu}
                    parent={postOpDxSearchMenuRef}
                    onOutsideClick={() => { setShowPostOpDxSearchMenu(false); }}
                  >
                    <div
                      style={{
                        width: '370px',
                        background: blue[900],
                        overflowY: 'auto',
                        paddingBottom: '1px',
                        borderRadius: '5px',
                        maxHeight: '200px'
                      }}
                    >
                      {postOpDxSearchItems}
                    </div>
                  </PopupMenu>
                }
              </div>
              {/* PostOp DX cards */}
              {postOpDxList.map(dx => {
                return (
                  <CaseUserCard
                    className='mt-1'
                    avatarImageUrl={dx.icon}
                    showCloseIcon
                    showAlert={dx.npi === 0}
                    noAvatar
                    key={dx.id}
                    dataId={dx.id}
                    onClose={(id) => {
                      setPostOpDxList((prevState) => {
                        return prevState.filter(el => el.id !== id);
                      });
                    }}
                  >
                    <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                      <div>
                        {`${dx.npi === 0 ? '?ICD10:' : ''} ${dx.description} ${dx.code !== 'N/A' ? `(${dx.code})` : ''}`}
                      </div>
                    </div>
                  </CaseUserCard>
                );
              })
              }
            </div>
            {/* Anesthesiologists */}
            <div ref={anesthesiologistsSectionRef}>
              <div
                className='fx mt-4'
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <CaseField
                  heading='Anesthesiologist(s)'
                  showAlert={showAnesthesiologistsAlert}
                  showIncomplete={isOtherAnesthesiologists}
                  showPlusIcon={false}
                />
                {/* None - Anesthesiologist */}
                {anesthesiologistsList.length === 0 &&
                  <div className='fx fx__v-center'>
                    <div className='crossed fx fx__hv-center'>
                      <input
                        type='checkbox'
                        className={`mr-1 ${css(styles.crossedCheckbox)}`}
                        value={isNoneAnesthesiologist}
                        style={{ border: `1px solid ${blue[200]}` }}
                        onChange={(e) => {
                          e.stopPropagation();
                          setIsNoneAnesthesiologist(!isNoneAnesthesiologist);
                        }}
                      />
                    </div>
                    <span
                      style={{ color: blue[200] }}
                    >
                      None
                    </span>
                  </div>
                }
              </div>
              {/* Anesthesiologists textbox value */}
              <div
                className='mt-0'
                style={{
                  border: `1px solid ${blue[700]}`,
                  borderRadius: '4px',
                  position: 'relative',
                  display: isNoneAnesthesiologist ? 'none' : ''
                }}
                ref={anesthesiologistsSearchMenuRef}
              >
                <TextInput
                  value={anesthesiologistValue}
                  placeholder="Add Anesthesiologist"
                  style={{
                    width: `${drawerWidth - 19}px`,
                    fontSize: '14px',
                    padding: '0 4px',
                    color: blue[200],
                    borderRadius: '4px'
                  }}
                  onChange={(e) => {
                    const { value } = e.target;
                    setAnesthesiologistValue(value);
                  }}
                  onClick={() => {
                    setShowAnesthesiologistsSearchMenu(true);
                  }}
                />
                {showAnesthesiologistsSearchMenu &&
                  <PopupMenu
                    top={35}
                    left={0}
                    zIndex='1550'
                    position="absolute"
                    visible={showAnesthesiologistsSearchMenu}
                    parent={anesthesiologistsSearchMenuRef}
                    onOutsideClick={() => { setShowAnesthesiologistsSearchMenu(false); }}
                  >
                    <div className={css(styles.searchPopupMenuContainer)}>
                      {anesthesiologistsSearchItems.length > 0 && anesthesiologistsSearchItems}
                      {anesthesiologistsSearchItems.length === 0 &&
                        <div className={css(styles.searchPopupMenuPlaceholder)}>
                          Search Anesthesiologist in the text box above
                        </div>
                      }
                    </div>
                  </PopupMenu>
                }
              </div>
              {/* Anesthesiologists cards */}
              {anesthesiologistsList.map(anesthesiologist => {
                return (
                  <CaseUserCard
                    className='mt-1'
                    avatarImageUrl={anesthesiologist.icon}
                    iconClass={anesthesiologist.iconClass}
                    showCloseIcon
                    showAlert={anesthesiologist.npi === 0}
                    key={anesthesiologist.user}
                    dataId={anesthesiologist.user}
                    onClose={(user) => {
                      setAnesthesiologistsList((prevState) => {
                        return prevState.filter(el => el.user !== user);
                      });
                    }}
                  >
                    <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                      <div>
                        {`${anesthesiologist.npi === 0 ? '?Anesthesiologist:' : ''} ${anesthesiologist.fullName}`}
                      </div>
                      {anesthesiologist.npi !== 0 && <div>{anesthesiologist.caseRelation}</div>}
                    </div>
                  </CaseUserCard>
                );
              })
              }
            </div>
            {/* CRNA */}
            <div ref={crnasSectionRef}>
              <div
                className='fx mt-4'
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <CaseField
                  heading='CRNA(s)/ CAA(s)'
                  showAlert={showCrnasAlert}
                  showIncomplete={isOtherCrans}
                  showPlusIcon={false}
                />
                {/* None - CRNA */}
                {crnasList.length === 0 &&
                  <div className='fx fx__v-center'>
                    <div className='crossed fx fx__hv-center'>
                      <input
                        type='checkbox'
                        className={`mr-1 ${css(styles.crossedCheckbox)}`}
                        style={{ border: `1px solid ${blue[200]}` }}
                        value={isNoneCrna}
                        onChange={(e) => {
                          e.stopPropagation();
                          setIsNoneCrna(!isNoneCrna);
                        }}
                      />
                    </div>
                    <span
                      style={{ color: blue[200] }}
                    >
                      None
                    </span>
                  </div>
                }
              </div>
              {/* CRNA value textbox */}
              <div
                ref={crnasSearchMenuRef}
                className='mt-0'
                style={{
                  border: `1px solid ${blue[700]}`,
                  borderRadius: '4px',
                  position: 'relative',
                  display: isNoneCrna ? 'none' : ''
                }}
              >
                <TextInput
                  value={crnaValue}
                  placeholder="Add CRNA"
                  style={{
                    width: `${drawerWidth - 19}px`,
                    fontSize: '14px',
                    padding: '0 4px',
                    color: blue[200],
                    borderRadius: '4px'
                  }}
                  onChange={(e) => {
                    const { value } = e.target;
                    setCrnaValue(value);
                  }}
                  onClick={() => setShowCrnasSearchMenu(true)}
                />
                {showCrnasSearchMenu &&
                  <PopupMenu
                    top={35}
                    left={0}
                    zIndex='1550'
                    position="absolute"
                    visible={showCrnasSearchMenu}
                    parent={crnasSearchMenuRef}
                    onOutsideClick={() => { setShowCrnasSearchMenu(false); }}
                  >
                    <div className={css(styles.searchPopupMenuContainer)}>
                      {crnasSearchItems.length > 0 && crnasSearchItems}
                      {crnasSearchItems.length === 0 &&
                        <div className={css(styles.searchPopupMenuPlaceholder)}>
                          Search CRNA in the text box above
                        </div>
                      }
                    </div>
                  </PopupMenu>
                }
              </div>
              {/* CRNA cards */}
              {crnasList.map(crna => {
                return (
                  <CaseUserCard
                    className='mt-1'
                    avatarImageUrl={crna.icon}
                    showCloseIcon
                    showAlert={crna.npi === 0}
                    iconClass={crna.iconClass}
                    key={crna.user}
                    dataId={crna.user}
                    onClose={(user) => {
                      setCrnasList((prevState) => {
                        return prevState.filter(el => el.user !== user);
                      });
                    }}
                  >
                    <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                      <div>
                        {`${crna.npi === 0 ? '?CRNA:' : ''} ${crna.fullName}`}
                      </div>
                      {crna.npi !== 0 && <div>{crna.caseRelation}</div>}
                    </div>
                  </CaseUserCard>
                );
              })
              }
            </div>
            {/* Anesthesia Procedures */}
            <div ref={anesthesiaProceduresSectionRef}>
              <div
                className='fx mt-4'
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <CaseField
                  heading='Anesthesia Procedures'
                  showAlert={showAnesthesiaProceduresAlert}
                  showIncomplete={isOtherAnesthesiaProcedures}
                  showPlusIcon={false}
                />
                {/* None - Anesthesia Procedure */}
                {anesthesiaProceduresList.length === 0 &&
                  <div className='fx fx__v-center crossed'>
                    <div className='crossed fx fx__hv-center'>
                      <input
                        type='checkbox'
                        className={`mr-1 ${css(styles.crossedCheckbox)}`}
                        value={isNoneAnesthesiaProcedure}
                        style={{ border: `1px solid ${blue[200]}` }}
                        onChange={(e) => {
                          e.stopPropagation();
                          setIsNoneAnesthesiaProcedure(!isNoneAnesthesiaProcedure);
                        }}
                      />
                    </div>
                    <span style={{ color: blue[200] }}>
                      None
                    </span>
                  </div>
                }
              </div>
              {/* Anesthesia procedures value textbox and search menu */}
              <div
                ref={anesthesiaProceduresSearchMenuRef}
                className='mt-0'
                style={{
                  border: `1px solid ${blue[700]}`,
                  borderRadius: '4px',
                  position: 'relative',
                  display: isNoneAnesthesiaProcedure ? 'none' : ''
                }}
              >
                <TextInput
                  value={anesthesiaProcedureValue}
                  placeholder="Search anesthesia procedures"
                  style={{
                    width: `${drawerWidth - 19}px`,
                    fontSize: '14px',
                    padding: '0 4px',
                    color: blue[200],
                    borderRadius: '4px'
                  }}
                  onChange={(e) => {
                    const { value } = e.target;
                    setAnesthesiaProcedureValue(value);
                  }}
                  onClick={() => {
                    getAllAnesthesiaProcedures();
                    getFavAnesthesiaProcedures();
                    setShowAnesthesiaProceduresSearchMenu(true);
                  }}
                />
                {showAnesthesiaProceduresSearchMenu &&
                  <PopupMenu
                    top={35}
                    left={0}
                    zIndex='1550'
                    position="absolute"
                    visible={showAnesthesiaProceduresSearchMenu}
                    parent={anesthesiaProceduresSearchMenuRef}
                    onOutsideClick={() => { setShowAnesthesiaProceduresSearchMenu(false); }}
                  >
                    <div className={css(styles.searchPopupMenuContainer)}>
                      <div
                        className='fx pl-2 pr-2'
                        style={{
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: '#184878'
                        }}
                      >
                        <TabsContainer
                          dropDownId="d2"
                          showPlusIcon={false}
                          shiftMethod='availableWidth'
                          containerWidth={370}
                          otherElementsWidth={0}
                          minWidth={206}
                          dropDownTop={39}
                          dropDownLeft={44}
                          dropDownHeaderStyles={{ minWidth: '170px', maxWidth: '370px', width: '100%' }}
                          containerStyles={{ width: '100%', background: '#184878' }}
                        >
                          <TabItem
                            tabKey="1"
                            key='8989'
                            parentKey="08787"
                            text=""
                            isActive={anesthesiaProceduresActiveTab === 0}
                            showTabIcon={false}
                            style={{
                              ...tabItemStyles,
                              borderBottomColor: anesthesiaProceduresActiveTab === 0 ? blue[500] : '#184878',
                              width: '50px',
                              minWidth: '50px',
                              maxWidth: '50px'
                            }}
                            textStyles={tabItemTextStyles}
                            onActivate={() => { setAnesthesiaProceduresActiveTab(0); }}
                          >
                            <div>All</div>
                          </TabItem>
                          <TabItem
                            tabKey="2"
                            key='4342'
                            parentKey="04542"
                            text=""
                            isActive={anesthesiaProceduresActiveTab === 1}
                            showTabIcon={false}
                            style={{
                              ...tabItemStyles,
                              borderBottomColor: anesthesiaProceduresActiveTab === 1 ? blue[500] : '#184878',
                              width: '80px',
                              minWidth: '80px',
                              maxWidth: '80px'
                            }}
                            textStyles={tabItemTextStyles}
                            onActivate={() => { setAnesthesiaProceduresActiveTab(1); }}
                          >
                            <div>
                              {`Favorites(${favAnesthesiaProcedures.length})`}
                            </div>
                          </TabItem>
                        </TabsContainer>
                        <div
                          className={`fx fx__hv-center ${css(styles.tabContainerCloseBtn)}`}
                          onClick={() => { setShowAnesthesiaProceduresSearchMenu(false); }}
                        >
                          <i
                            className='fa fa-times'
                            onClick={() => { setShowAnesthesiaProceduresSearchMenu(false); }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          height: '200px',
                          maxHeight: '200px',
                          overflowY: 'scroll',
                          padding: '8px',
                          background: blue[900]
                        }}
                      >
                        {anesthesiaProceduresActiveTab === 0 && anesthesiaProceduresSearchItems.length > 0 && anesthesiaProceduresSearchItems}
                        {anesthesiaProceduresActiveTab === 1 &&
                          favAnesthesiaProcedures.map(el => {
                            return (
                              <div
                                key={el._id}
                                className={`fx fx__v-center ${css(styles.menuItem)}`}
                              >
                                <div
                                  className='ml-2'
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%'
                                  }}
                                >
                                  <div
                                    style={{
                                      height: '40px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      flexGrow: '1'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowAnesthesiaProceduresSearchMenu(false);
                                      setAnesthesiaProcedureValue('');
                                      setAnesthesiaProceduresList((prevState) => {
                                        if (prevState.findIndex(item => item.id === el._id) < 0) {
                                          return prevState.concat({
                                            id: el._id,
                                            text: `${el.description}`,
                                            code: '',
                                            description: `${el.description}`,
                                            npi: 1
                                          });
                                        } else {
                                          return prevState;
                                        }
                                      });
                                    }}
                                  >
                                    {el.description}
                                  </div>
                                  <div className={css(styles.menuItemStarContainer, styles.isFav)}>
                                    <i
                                      className='fa fa-star'
                                      onClick={() => {
                                        removeFavAnesthesiaProcedure(el._id);
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })
                        }
                        {anesthesiaProceduresActiveTab === 0 && anesthesiaProceduresSearchItems.length === 0 &&
                          <div className={css(styles.searchPopupMenuPlaceholder)}>
                            Search Anesthesia Procedure in the text box above
                          </div>
                        }
                      </div>
                    </div>
                  </PopupMenu>
                }
              </div>
              {/* Anesthesia Procedures cards */}
              {anesthesiaProceduresList.map(procedure => {
                return (
                  <CaseUserCard
                    className='mt-1'
                    avatarImageUrl={procedure.icon}
                    showCloseIcon
                    showAlert={procedure.npi === 0}
                    noAvatar
                    key={procedure.id}
                    dataId={procedure.id}
                    onClose={(id) => {
                      setAnesthesiaProceduresList((prevState) => {
                        return prevState.filter(el => el.id !== id);
                      });
                    }}
                  >
                    <div style={{ fontSize: '12px', marginLeft: '8px' }}>
                      <div>{`${procedure.npi === 0 ? '?Anesthesia Procedure:' : ''} ${procedure.description}`}</div>
                    </div>
                  </CaseUserCard>
                );
              })
              }
            </div>
            {/* ASA Score */}
            <div className='mt-4'>
              <div
                className='fx'
                style={{ alignItems: 'center', justifyContent: 'flex-start' }}
              >
                {showAsaAlert
                  ? <svg
                    width="16"
                    height="16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fillRule="evenodd" clipRule="evenodd" d="M15.997 0H0v16h16V0h-.003Zm0 0v16l-5.622-8 5.622-8Z" fill="#FF6060" />
                  </svg>
                  : tickIcon
                }
                <div className={`ml-2 ${css(styles.sectionHeading)}`}>
                  ASA Score
                </div>
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={45}
                  height={32}
                  className={`mr-2 ${showAsaAlert ? css(styles.pressedBtnAlert) : ''}`}
                  onClick={() => {
                    setAsaScore('1');
                  }}
                  isPressed={asaScore === '1'}
                >
                  1
                </PressedButton>
                <PressedButton
                  width={45}
                  height={32}
                  className={`mr-2 ${showAsaAlert ? css(styles.pressedBtnAlert) : ''}`}
                  onClick={() => {
                    setAsaScore('2');
                  }}
                  isPressed={asaScore === '2'}
                >
                  2
                </PressedButton>
                <PressedButton
                  width={45}
                  height={32}
                  className={`mr-2 ${showAsaAlert ? css(styles.pressedBtnAlert) : ''}`}
                  onClick={() => {
                    setAsaScore('3');
                  }}
                  isPressed={asaScore === '3'}
                >
                  3
                </PressedButton>
                <PressedButton
                  width={45}
                  height={32}
                  className={`mr-2 ${showAsaAlert ? css(styles.pressedBtnAlert) : ''}`}
                  onClick={() => {
                    setAsaScore('4');
                  }}
                  isPressed={asaScore === '4'}
                >
                  4
                </PressedButton>
                <PressedButton
                  width={45}
                  height={32}
                  className={`mr-2 ${showAsaAlert ? css(styles.pressedBtnAlert) : ''}`}
                  onClick={() => {
                    setAsaScore('5');
                  }}
                  isPressed={asaScore === '5'}
                >
                  5
                </PressedButton>
                <PressedButton
                  width={45}
                  height={32}
                  className={`mr-2 ${showAsaAlert ? css(styles.pressedBtnAlert) : ''}`}
                  onClick={() => {
                    setAsaScore('6');
                  }}
                  isPressed={asaScore === '6'}
                >
                  6
                </PressedButton>
                <div
                  style={{
                    width: '2px',
                    height: '32px',
                    background: '#003C79',
                    marginRight: '5px'
                  }}
                />
                <PressedButton
                  width={32}
                  height={32}
                  className={`mr-2 ${showAsaAlert ? css(styles.pressedBtnAlert && styles.pressedBtn) : ''}`}
                  style={{ borderRadius: '50%' }}
                  onClick={() => {
                    setIsEmergencyAsaScore(!isEmergencyAsaScore);
                  }}
                  isPressed={isEmergencyAsaScore}
                >
                  E
                </PressedButton>
              </div>
            </div>
            {/* Anesthesia Type */}
            <div className='mt-4'>
              <div className='fx' style={{ alignItems: 'center', justifyContent: 'flex-start' }}>
                {showAnesthesiaTypeAlert
                  ? <svg
                    width="16"
                    height="16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fillRule="evenodd" clipRule="evenodd" d="M15.997 0H0v16h16V0h-.003Zm0 0v16l-5.622-8 5.622-8Z" fill="#FF6060" />
                  </svg>
                  : tickIcon
                }
                <div className={`ml-2 ${css(styles.sectionHeading)}`}>
                  Anesthesia Type
                </div>
              </div>
              {/* Top Row */}
              <div className='fx mt-2'>
                <PressedButton
                  width={80}
                  className={css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}
                  onClick={() => {
                    setIsGaEtt(!isGaEtt);
                  }}
                  isPressed={isGaEtt}
                >
                  GA-ETT
                </PressedButton>
                <PressedButton
                  width={80}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsGaLma(!isGaLma);
                  }}
                  isPressed={isGaLma}
                >
                  GA-LMA
                </PressedButton>
                <PressedButton
                  width={80}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsMac(!isMac);
                  }}
                  isPressed={isMac}
                >
                  MAC<br />99141
                </PressedButton>
                <PressedButton
                  width={80}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsEpidural(!isEpidural);
                  }}
                  isPressed={isEpidural}
                >
                  Epidural
                </PressedButton>
              </div>
              {/* Bottom Row */}
              <div className='fx mt-2'>
                <PressedButton
                  width={80}
                  className={css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}
                  onClick={() => {
                    setIsSab(!isSab);
                  }}
                  isPressed={isSab}
                >
                  SAB
                </PressedButton>
                <PressedButton
                  width={80}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsRegional(!isRegional);
                  }}
                  isPressed={isRegional}
                >
                  Regional
                </PressedButton>
                <PressedButton
                  width={80}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsLocal(!isLocal);
                  }}
                  isPressed={isLocal}
                >
                  Local
                </PressedButton>
                <PressedButton
                  width={80}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsOther(!isOther);
                  }}
                  isPressed={isOther}
                >
                  Other
                </PressedButton>
              </div>
            </div>
            {/* Anesthesia Position */}
            <div className='mt-4'>
              <div className='fx' style={{ alignItems: 'center', justifyContent: 'flex-start' }}>
                {showPositionAlert
                  ? <svg
                    width="16"
                    height="16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fillRule="evenodd" clipRule="evenodd" d="M15.997 0H0v16h16V0h-.003Zm0 0v16l-5.622-8 5.622-8Z" fill="#FF6060" />
                  </svg>
                  : tickIcon
                }
                <div className={`ml-2 ${css(styles.sectionHeading)}`}>
                  Position (Check ALL That Apply)
                </div>
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={165}
                  className={css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}
                  onClick={() => {
                    setIsSupine(!isSupine);
                  }}
                  isPressed={isSupine}
                >
                  Supine
                </PressedButton>
                <PressedButton
                  width={165}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsProne(!isProne);
                  }}
                  isPressed={isProne}
                >
                  Prone
                </PressedButton>
                <PressedButton
                  width={165}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsLithotomy(!isLithotomy);
                  }}
                  isPressed={isLithotomy}
                >
                  Lithotomy
                </PressedButton>
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={165}
                  className={css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}
                  onClick={() => {
                    setIsLateralLeft(!isLateralLeft);
                  }}
                  isPressed={isLateralLeft}
                >
                  Lateral-Left
                </PressedButton>
                <PressedButton
                  width={165}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsLateralRight(!isLateralRight);
                  }}
                  isPressed={isLateralRight}
                >
                  Lateral-Right
                </PressedButton>
                <PressedButton
                  width={165}
                  className={`ml-2 ${css(styles.anesModBtns, showPositionAlert && styles.pressedBtnAlert)}`}
                  onClick={() => {
                    setIsSitting(!isSitting);
                  }}
                  isPressed={isSitting}
                >
                  Sitting
                </PressedButton>
              </div>
            </div>
            {/* Anesthesia Modifiers */}
            <div className='mt-4'>
              <div className={css(styles.sectionHeading)}>
                Anesthesia Modifiers (Leave blank if none)
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={174}
                  className={css(styles.anesModBtns)}
                  onClick={() => {
                    setIsFieldAvoidance(!isFieldAvoidance);
                  }}
                  isPressed={isFieldAvoidance}
                >
                  Field Avoidance
                </PressedButton>
                <PressedButton
                  width={174}
                  className={`ml-2 ${css(styles.anesModBtns)}`}
                  onClick={() => {
                    setIsExtremeAge(!isExtremeAge);
                  }}
                  isPressed={isExtremeAge}
                >
                  <div style={{ fontSize: '12px', textAlign: 'center', lineHeight: '12px' }}>
                    <div>Extreme Age (&lt;1y OR &gt; 70)</div>
                  </div>
                </PressedButton>
              </div>
              <div className='fx mt-2'>
                <PressedButton
                  width={174}
                  className={css(styles.anesModBtns)}
                  onClick={() => {
                    setIsControlledHypotension(!isControlledHypotension);
                  }}
                  isPressed={isControlledHypotension}
                >
                  <div style={{ fontSize: '12px', textAlign: 'center', lineHeight: '12px' }}>
                    <div>Controlled Hypotension</div>
                  </div>
                </PressedButton>
                <PressedButton
                  width={174}
                  className={`ml-2 ${css(styles.anesModBtns)}`}
                  onClick={() => {
                    setIsControlledHypothermia(!isControlledHypothermia);
                  }}
                  isPressed={isControlledHypothermia}
                >
                  <div style={{ fontSize: '12px', textAlign: 'center', lineHeight: '12px' }}>
                    <div>Controlled Hypothermia</div>
                  </div>
                </PressedButton>
              </div>
            </div>
            {/* Anesthesia Times */}
            <div className='mt-4' style={{ position: 'relative' }}>
              <div className='fx' style={{ alignItems: 'center', justifyContent: 'flex-start' }}>
                {(showAnesStartDateAlert || showAnesEndDateAlert || showAnesStartTimeAlert || showAnesEndTimeAlert || showAnesthesiaTimeAlert)
                  ? <svg
                    width="16"
                    height="16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path fillRule="evenodd" clipRule="evenodd" d="M15.997 0H0v16h16V0h-.003Zm0 0v16l-5.622-8 5.622-8Z" fill="#FF6060" />
                  </svg>
                  : tickIcon
                }
                <div className={`ml-2 ${css(styles.sectionHeading)}`}>
                  Times
                  {showAnesthesiaTimeAlert &&
                    <span className={`ml-2 ${css(styles.requiredHeadingMsg)}`}>
                      “Start” must be earlier than “End”
                    </span>
                  }
                </div>
              </div>
              {/* Anesthesia Date Time */}
              <div className='fx mt-2' style={{ justifyContent: 'space-between', fontSize: '14px' }}>
                {/* Anesthesia Start */}
                <div style={{ fontSize: '12px' }}>
                  <div className={css(styles.sectionSubHeading)}>
                    Anesthesia Start
                  </div>
                  <div className='fx'>
                    <div style={{ borderRadius: '2px', background: showAnesStartDateAlert ? '#804254' : '' }}>
                      <DatePicker
                        date={anesthesiaStartDate}
                        month={anesthesiaStartMonth}
                        year={anesthesiaStartYear}
                        color={isAnesStartDatePristine ? salmon[500] : blue[500]}
                        noUnderline
                        onChangeDate={handleOnChangeAnesthesiaStartDate}
                        isPrisitine={isAnesStartDatePristine}
                      />
                    </div>
                    <div className='ml-1' style={{ border: '2px', background: showAnesStartTimeAlert ? '#804254' : '' }}>
                      <TimePicker
                        className='pl-1 pr-2'
                        hour={anesthesiaStartHour}
                        minute={anesthesiaStartMinute}
                        color={isAnesthesiaStartTimePristine ? salmon[500] : blue[500]}
                        noUnderline
                        timeFormat='24H'
                        timePeriod='AM'
                        isPristine={isAnesthesiaStartTimePristine}
                        onChangeTime={handleOnChangeAnesthesiaStartTime}
                        disableTimeFormat
                      />
                    </div>
                  </div>
                </div>

                <div className='ml-0 mr-2' style={{ paddingTop: '15px' }}>
                  to
                </div>

                {/* Anesthesia End */}
                <div style={{ fontSize: '12px', maxWidth: '123px' }}>
                  <div className={css(styles.sectionSubHeading)}>
                    Anesthesia End
                  </div>
                  <div className='fx'>
                    <div style={{ borderRadius: '2px', background: showAnesEndDateAlert ? '#804254' : '' }}>
                      <DatePicker
                        left={0}
                        right={-130}
                        date={anesthesiaEndDate}
                        color={isAnesEndDatePristine ? salmon[500] : blue[500]}
                        noUnderline
                        month={anesthesiaEndMonth}
                        year={anesthesiaEndYear}
                        onChangeDate={handleOnChangeAnesthesiaEndDate}
                        isPrisitine={isAnesEndDatePristine}
                      />
                    </div>
                    <div className='ml-1' style={{ borderRadius: '2px', background: showAnesEndTimeAlert ? '#804254' : '' }}>
                      <TimePicker
                        className='pl-1 pr-1'
                        left={null}
                        right={1}
                        hour={anesthesiaEndHour}
                        minute={anesthesiaEndMinute}
                        color={isAnesthesiaEndTimePristine ? salmon[500] : blue[500]}
                        noUnderline
                        timeFormat='24H'
                        timePeriod='AM'
                        isPristine={isAnesthesiaEndTimePristine}
                        onChangeTime={handleOnChangeAnesthesiaEndTime}
                        disableTimeFormat
                      />
                    </div>
                  </div>
                </div>
                {/* Total Anesthesia Time */}
                <div style={{ fontSize: '12px' }}>
                  <div className={css(styles.sectionSubHeading)}>
                    Total Time
                  </div>
                  <div>
                    {!isAnesthesiaStartTimePristine && !isAnesthesiaEndTimePristine && anesthesiaTime > 0
                      ? `${anesthesiaTime} mins`
                      : '-'
                    }
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom spacer */}
            <div style={{ height: '300px' }} />
          </div>
          {/* Footer */}
          <div className={css(styles.drawerFooter)}>
            {/* Cancel Button */}
            <Button
              btnType="primary"
              className="fx fx__hv-center"
              isOutlineBtn
              onClick={props.onClose}
            >
              Cancel
            </Button>
            {/* Create Case Button */}
            <Button
              btnType="primary"
              className="ml-2 fx fx__hv-center"
              isDisabled={!canCreateCase}
              onClick={createCase}
            >
              Create Case
            </Button>
          </div>
        </div>
      }
    </Drawer>
  );
};

export default NewCaseDrawer;
